// Thin typed client for the legends API. All calls go through /api (the Vite
// dev server / nginx proxies to the API), so the frontend is origin-agnostic.
//
// Impersonation (ADR 0007): an impersonation tab keeps a short-lived token in
// sessionStorage (per-tab) and sends it on every request via the
// X-Legends-Impersonation header, which the API prefers over the cookie. The
// operator's cookie session is never touched.

import { gaTrack, gaEvent } from "./ga";

export interface HealthReport {
  api: string;
  version: string;
  dependencies?: Record<string, { ok: boolean; detail?: string }>;
}

export interface HelloResponse {
  message: string;
}

export interface AuthUser {
  username: string;
  displayName?: string;
  email?: string;
}

export interface AuthProvider {
  id: string;
  name: string;
  enabled: boolean;
}

/** The four persona views a tenant exposes (ADR 0007). */
export type ViewName = "public" | "consumer" | "legend" | "director";

export interface ViewImpersonation {
  tenant: string;
  view: ViewName;
}

export interface AuthStatus {
  state: "unauthenticated" | "authenticated";
  user?: AuthUser;
  providers: AuthProvider[];
  /** Present when the request carried an impersonation token — the operator's username. */
  impersonator?: string;
  /** Present when the request carried a view-impersonation token. */
  viewImpersonation?: ViewImpersonation;
}

export type TenantRole = "member" | "admin" | "super_admin" | "legend" | "director" | "consumer";

export interface TenantGrant {
  id: string;
  name: string;
  roles: TenantRole[];
}

/** The caller's authorization snapshot from GET /authz/me. */
export interface AuthzMe {
  superAdmin: boolean;
  /** EXACTLY the seeded root account (ADR 0223) — stricter than superAdmin; gates the
   *  comp ticket grant. The server always sends it; optional here so existing AuthzMe
   *  mocks keep compiling (absent ⇒ treated as false). */
  rootAdmin?: boolean;
  canImpersonate: boolean;
  canManageAuthz: boolean;
  /** Pricing authority (ADR 0037): may create/edit/delete ticket types + prices. */
  catalogWrite: boolean;
  /** VIP watch access (LSC-549): watch ticketed live + eligible replays without a
   *  holding. Optional so existing AuthzMe mocks keep compiling (absent ⇒ false). */
  vip?: boolean;
  /** The legend this account IS, when linked (ADR 0038) — else absent. */
  legendId?: string;
  /** That legend's headline cave, when it has one (ADR 0038). */
  legendCaveId?: string;
  tenants: TenantGrant[];
}

export interface Tenant {
  id: string;
  name: string;
}

export interface TenantMember {
  username: string;
  roles: string[];
}

/** A host-bound public site owned by a tenant. */
export interface Cave {
  id: string;
  tenant: string;
  name: string;
  host: string;
  /** Whether this cave's operators may add their own pre/post-roll items
   *  (ADR 0052). Super-admin sets it; drives the cave console's Pre/Post-roll tab. */
  allowRollContrib?: boolean;
  /** The Cave (front)'s opaque branding JSON (CF-4, LSC-372) — "" = default
   *  treatment. Present on a front-resolved public cave view (by host). */
  branding?: string;
  /** True for a retired cave (ADR 0067). Only present on the super-admin caves
   *  list fetched with includeArchived; the normal views omit archived caves. */
  archived?: boolean;
  /** Cave-level recording opt-out (ADR 0080): null = inherit the platform default;
   *  true/false = force recording on/off for this cave's CaveCasts. */
  recordDefault?: boolean | null;
}

/** A cave event — a dated show, single or part of a recurring series (ADR 0008). */
export interface CaveEvent {
  id: string;
  /** The Cave (front) this event belongs to + shows on (CF-5, LSC-372);
   *  == the backend id for the default front. */
  caveFrontId?: string;
  title: string;
  /** RFC3339 UTC start instant. */
  startsAt: string;
  durationMinutes: number;
  /** Present (non-empty) when the event belongs to a recurring series. */
  seriesId?: string;
  /** Event-level recording opt-out (ADR 0080): null = inherit the cave default;
   *  true/false = force recording on/off for this occurrence (lowest override). */
  record?: boolean | null;
  /** Event-level Ask-the-Legend default (ADR 0093): true disables Ask for this
   *  event's shows (seeds program.askDisabled at go-live); false/absent = Ask on. */
  askDisabled?: boolean;
  /** Event-level live-delivery choice (ADR 0140): "" (or absent) inherits the
   *  platform default; "webrtc" = low-latency interactive; "hls" = segmented
   *  (AirPlay/PiP/Cast). */
  delivery?: Delivery;
  /** Set (RFC3339) once the event's broadcast has ended — auto-archived (ADR 0094);
   *  absent = active. Going live again clears it. */
  archivedAt?: string | null;
  /** A "false-start" (LSC-641/ADR 0248): this event's broadcast is `live` but never
   *  reached a TAKE past a short grace — a fan sees it as "Live Now" that never airs
   *  (an accidental console-open / Go-Live→Exit). Set only on the operator Events read;
   *  drives the per-event "Reset to not-started" reclaim control. */
  liveNotAired?: boolean;
  /** Admin-editable presentation order within the cave (LSC-572 / ADR 0251); 0 =
   *  unordered (natural starts_at order). Drives the reorder affordance. */
  displayOrder?: number;
  /** Event-level private-rehearsal flag (ADR 0259): true hides the event from every
   *  PUBLIC read (events, ticket eventIds/coverage, discovery, catalog) and from
   *  admission, yet it stays producible for crew. Operator-facing; a public event is
   *  never rehearsal (filtered out), so it reads false there. */
  rehearsal?: boolean;
}

/** The four ways a ticket type covers events (ADR 0008). */
export type TicketKind = "single" | "season" | "all_access" | "custom";

/** A cave ticket type — its coverage snapshot summarized by eventCount/nextEventAt. */
export interface TicketType {
  id: string;
  name: string;
  kind: TicketKind;
  /** Full (undiscounted) price for the whole covered set. */
  priceCents: number;
  /**
   * Current price after pro-rating for CaveCasts already gone (ADR 0161) — what a
   * buyer pays today. Equals priceCents when nothing has passed.
   */
  proratedPriceCents: number;
  /** Upcoming (not-yet-started) covered events. */
  eventCount: number;
  /** Whole non-archived covered set (passed + upcoming) — the pro-rating denominator. */
  totalEventCount: number;
  /** RFC3339 UTC start of the soonest covered event, when any remain upcoming. */
  nextEventAt?: string;
  /** Ids of the upcoming covered events (same set as eventCount), so a client can
   *  name what a single/custom ticket covers (LSC-356). Empty when none upcoming. */
  eventIds?: string[];
  /** Admin-editable presentation order within the cave (LSC-572 / ADR 0251); 0 =
   *  unordered (natural created-at order). Drives the reorder affordance. */
  displayOrder?: number;
}

/** One row of a cave's access list — a tenant user and how they reach cave config. */
export interface CaveAccessRow {
  username: string;
  displayName?: string;
  roles: string[];
  /** Access comes from a tenant role (legend/admin) — read-only, not toggleable. */
  viaRole: boolean;
  /** A direct per-cave grant is in place (toggleable). */
  direct: boolean;
  /** Holds lobby-publish authority for this cave (LSC-260). */
  canPublish: boolean;
  /** Publish authority is via admin role (read-only) vs a per-cave grant (toggleable). */
  publishViaRole: boolean;
}

/** A catalog entry — one ticket type paired with its owning cave (ADR 0008). */
export interface CatalogTicket {
  cave: { id: string; name: string; host: string };
  ticket: TicketType;
}

/** Body for POST /caves/:id/events. */
export interface NewCaveEvent {
  title: string;
  /** RFC3339 UTC. */
  startsAt: string;
  durationMinutes: number;
  recurrence?: { intervalDays: number; count: number };
  /** Which Cave (front) the event belongs to (CF-5, LSC-372); omit for the
   *  backend's default front. */
  caveFrontId?: string;
}

/** A Cave (front): a public storefront in front of a Cave Backend (CF-5, LSC-372). */
export interface CaveFront {
  id: string;
  backendId: string;
  host: string;
  name: string;
  branding?: string;
  /** The default front (id == backend id) — cannot be archived. */
  default: boolean;
  archived: boolean;
}

export function fetchCaveFronts(caveId: string): Promise<CaveFront[]> {
  return getJSON<{ fronts: CaveFront[] }>(
    `/api/v1/caves/${encodeURIComponent(caveId)}/fronts`,
  ).then((b) => b.fronts);
}

export function createCaveFront(
  caveId: string,
  body: { name: string; slug?: string; branding?: string },
): Promise<{ front: CaveFront; dns: string }> {
  return sendJSON("POST", `/api/v1/caves/${encodeURIComponent(caveId)}/fronts`, body);
}

export function updateCaveFront(
  caveId: string,
  frontId: string,
  body: { name?: string; branding?: string },
): Promise<{ front: CaveFront }> {
  return sendJSON(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/fronts/${encodeURIComponent(frontId)}`,
    body,
  );
}

export function setCaveFrontArchived(
  caveId: string,
  frontId: string,
  archived: boolean,
): Promise<void> {
  const verb = archived ? "archive" : "unarchive";
  return sendJSON(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/fronts/${encodeURIComponent(frontId)}/${verb}`,
    {},
  ).then(() => undefined);
}

/** Body for POST /caves/:id/ticket-types — the extra field depends on kind. */
export interface NewTicketType {
  name: string;
  kind: TicketKind;
  priceCents: number;
  /** single */
  eventId?: string;
  /** season */
  seriesId?: string;
  /** all_access */
  windowWeeks?: number;
  /** custom */
  eventIds?: string[];
}

/** Per-user authz detail from GET /authz/users/:username. */
export interface UserAuthz {
  username: string;
  /** domain → roles held there ("*" is the global domain). */
  roles: Record<string, string[]>;
  viewImpersonations: ViewImpersonation[];
  grantableRoles: string[];
  views: ViewName[];
}

/** One tenant's worth of views the caller may impersonate. */
export interface ImpersonableTenantViews {
  tenant: Tenant;
  views: ViewName[];
}

/** API failure carrying the HTTP status so callers can branch (403 vs down). */
export class ApiError extends Error {
  status: number;
  /** For lobby validation 400s: the offending module id, so the editor can highlight
   *  it (LSC-258). Undefined for other errors. */
  moduleId?: string;

  constructor(status: number, message: string, moduleId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.moduleId = moduleId;
  }
}

// ── impersonation token plumbing ─────────────────────────────────────────────

const IMP_KEY = "legends_imp";

/** The tab's impersonation token, if this tab is an impersonation tab. */
export function impersonationToken(): string | null {
  return sessionStorage.getItem(IMP_KEY);
}

export function clearImpersonationToken(): void {
  sessionStorage.removeItem(IMP_KEY);
}

/**
 * Adopt a freshly launched impersonation: the operator's tab opens us at
 * /#imp=<token>; move the token into per-tab sessionStorage and strip the
 * fragment so it never lingers in the address bar or history.
 */
export function adoptImpersonationHash(): void {
  const hash = window.location.hash;
  if (hash.startsWith("#imp=")) {
    sessionStorage.setItem(IMP_KEY, decodeURIComponent(hash.slice("#imp=".length)));
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

/**
 * Central fetch: every API call attaches the impersonation header when this
 * tab holds a token. A 401 with a token means the token expired — clear it and
 * reload so the tab falls back to the operator's cookie session.
 */
async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = impersonationToken();
  const merged: RequestInit | undefined = token
    ? {
        ...init,
        headers: {
          ...(init?.headers as Record<string, string> | undefined),
          "X-Legends-Impersonation": token,
        },
      }
    : init;
  const resp = await fetch(path, merged);
  if (resp.status === 401 && token) {
    clearImpersonationToken();
    window.location.reload();
  }
  return resp;
}

async function getJSON<T>(path: string, tenant?: string): Promise<T> {
  const resp = await apiFetch(path, tenant ? { headers: { "X-Legends-Tenant": tenant } } : undefined);
  if (!resp.ok) {
    throw new ApiError(resp.status, `API returned ${resp.status}`);
  }
  return (await resp.json()) as T;
}

async function sendJSON<T>(method: string, path: string, body?: unknown): Promise<T> {
  const resp = await apiFetch(path, {
    method,
    ...(body !== undefined
      ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
      : {}),
  });
  if (!resp.ok) {
    let message = `API returned ${resp.status}`;
    let moduleId: string | undefined;
    try {
      const data = (await resp.json()) as { error?: string; moduleId?: string };
      if (data.error) message = data.error;
      if (data.moduleId) moduleId = data.moduleId;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new ApiError(resp.status, message, moduleId);
  }
  return (await resp.json()) as T;
}

// ── public (unauthenticated) ─────────────────────────────────────────────────

/** The full cave catalog. */
export function fetchCaves(): Promise<Cave[]> {
  return getJSON<{ caves: Cave[] }>("/api/v1/public/caves").then((b) => b.caves);
}

/** Resolve the current hostname to a cave; null when this host is not a cave. */
export async function fetchCaveForHost(host: string): Promise<Cave | null> {
  const resp = await apiFetch(`/api/v1/public/cave?host=${encodeURIComponent(host)}`);
  if (resp.status === 404) return null;
  if (!resp.ok) {
    throw new ApiError(resp.status, `API returned ${resp.status}`);
  }
  const body = (await resp.json()) as { cave: Cave };
  return body.cave;
}

/** A cave's upcoming events (public storefront). */
export function fetchPublicCaveEvents(id: string): Promise<CaveEvent[]> {
  return getJSON<{ events: CaveEvent[] }>(
    `/api/v1/public/cave/${encodeURIComponent(id)}/events`,
  ).then((b) => b.events);
}

/** A cave's ticket types (public storefront). */
export function fetchPublicCaveTickets(id: string): Promise<TicketType[]> {
  return getJSON<{ ticketTypes: TicketType[] }>(
    `/api/v1/public/cave/${encodeURIComponent(id)}/tickets`,
  ).then((b) => b.ticketTypes);
}

/** Every cave's tickets — the base catalog. */
export function fetchCatalogTickets(): Promise<CatalogTicket[]> {
  return getJSON<{ tickets: CatalogTicket[] }>("/api/v1/public/tickets").then((b) => b.tickets);
}

// ── core ─────────────────────────────────────────────────────────────────────

export function fetchHealth(): Promise<HealthReport> {
  return getJSON<HealthReport>("/api/v1/health");
}

/** Hello is tenant-scoped: pass the selected tenant id via X-Legends-Tenant. */
export function fetchHello(tenant?: string): Promise<HelloResponse> {
  return getJSON<HelloResponse>("/api/v1/hello", tenant);
}

export function fetchAuthStatus(): Promise<AuthStatus> {
  return getJSON<AuthStatus>("/api/v1/auth/status");
}

export function fetchAuthz(): Promise<AuthzMe> {
  return getJSON<AuthzMe>("/api/v1/authz/me");
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const resp = await apiFetch("/api/v1/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  if (resp.status === 401) {
    throw new ApiError(401, "Invalid credentials");
  }
  if (resp.status === 403) {
    // Local account exists but its email isn't verified (LSC-554). Surface the
    // server message + the 403 status so LoginScreen can offer "resend verification".
    let msg = "Email not verified — check your inbox for the verification link.";
    try {
      const d = (await resp.json()) as { error?: string };
      if (d.error) msg = d.error;
    } catch {
      /* keep default */
    }
    throw new ApiError(403, msg);
  }
  if (!resp.ok) {
    throw new ApiError(resp.status, `API returned ${resp.status}`);
  }
  const body = (await resp.json()) as { user: AuthUser };
  return body.user;
}

export async function logout(): Promise<void> {
  const resp = await apiFetch("/api/v1/auth/logout", { method: "POST" });
  if (!resp.ok) {
    throw new Error(`API returned ${resp.status}`);
  }
}

// Local Fan registration + email verification (LSC-554 / ADR 0233).

/** Register a local Fan account (email + password). Returns once the verification
 *  email is dispatched — the fan must confirm it, then sign in. Throws ApiError(409)
 *  if the email is already in use. */
export function register(
  email: string,
  password: string,
  displayName?: string,
): Promise<{ status: string; email: string }> {
  return sendJSON("POST", "/api/v1/auth/register", { email, password, displayName });
}

/** Confirm a local account with the emailed token; on success the fan is signed in
 *  (session cookie set) and the user is returned. Throws ApiError(400) on a bad/expired token. */
export function verifyEmail(token: string): Promise<AuthUser> {
  return sendJSON<{ user: AuthUser }>("POST", "/api/v1/auth/verify-email", { token }).then((b) => b.user);
}

/** Resend the verification email for an unverified local account. Always resolves
 *  (the server never reveals whether the email is registered). */
export function resendVerification(email: string): Promise<void> {
  return sendJSON("POST", "/api/v1/auth/resend-verification", { email }).then(() => undefined);
}

/** Report a Fan playback problem + the session's diagnostic ring buffer (LSC-598).
 *  Best-effort — resolves with the server's short reference (PBK-…) on success, or null
 *  on any failure (a telemetry failure must never disrupt Watch). Carries no secrets. */
export function submitPlaybackReport(p: {
  sessionRef: string;
  broadcastId: string;
  delivery: string;
  symptom: string;
  events: { t: number; kind: string; detail?: string }[];
}): Promise<string | null> {
  return sendJSON<{ reference: string }>("POST", "/api/v1/analytics/playback-report", p)
    .then((b) => b.reference)
    .catch(() => null);
}

/** Request a password reset (LSC-614). Always resolves generically (429 aside) — the
 *  server never reveals whether the email is registered, SSO-only, or unverified; a
 *  reset link is emailed only for a verified LOCAL account. */
export function forgotPassword(email: string): Promise<void> {
  return sendJSON("POST", "/api/v1/auth/forgot-password", { email }).then(() => undefined);
}

/** Redeem a reset token with a new password (LSC-614). On success the fan is signed in
 *  (fresh session cookie set; all prior sessions invalidated) and the user is returned.
 *  Throws ApiError(400) on a bad/expired/reused token or a password below policy. */
export function resetPassword(token: string, password: string): Promise<AuthUser> {
  return sendJSON<{ user: AuthUser }>("POST", "/api/v1/auth/reset-password", { token, password }).then((b) => b.user);
}

// ── cave configuration (needs cave access — ADR 0008) ────────────────────────

/** The caves the caller may configure (legend/admin of the tenant, or a grant).
 *  With includeArchived (super-admin), retired caves are included too, each
 *  carrying `archived` so the caller can tell them apart (ADR 0067). */
export function fetchConfigurableCaves(includeArchived?: boolean): Promise<Cave[]> {
  const q = includeArchived ? "?archived=1" : "";
  return getJSON<{ caves: Cave[] }>(`/api/v1/caves${q}`).then((b) => b.caves);
}

/** A CaveCast the caller may PRODUCE via an event-scoped grant (ADR 0038/0065),
 *  resolved to its event + cave — the event-only Director entry point. Present
 *  even when the caller has no configurable caves. */
export interface AssignedEvent {
  eventId: string;
  caveId: string;
  caveName: string;
  caveHost: string;
  title: string;
  /** RFC3339 UTC start instant. */
  startsAt: string;
}

/** The events the caller may produce via a direct event_config grant (ADR 0065),
 *  independent of cave membership. */
export function fetchAssignedEvents(): Promise<AssignedEvent[]> {
  return getJSON<{ events: AssignedEvent[] }>("/api/v1/me/events").then((b) => b.events);
}

/** The result of provisioning a cave (ADR 0023). */
export interface CreatedCave {
  cave: Cave;
  host: string;
  /** "created" | "skipped" | "failed" — the DNS provisioning outcome. */
  dnsStatus: string;
}

/** Super-admin: provision a cave in one step (tenant + cave + DNS + optional
 *  headline legend). */
export function createCave(input: { name: string; slug?: string; legendId?: string }): Promise<CreatedCave> {
  return sendJSON<CreatedCave>("POST", "/api/v1/caves", input);
}

/** The fan-facing public address a name/slug would produce + whether it's free
 *  (LSC-328) — server-derived so the preview never disagrees with what a create does. */
export interface CaveHostPreview {
  slug: string;
  domain: string;
  host?: string;
  available: boolean;
  /** plain-language explanation when not available (empty when available). */
  reason?: string;
}

/** Super-admin: preview the public address for a cave name (optionally an explicit
 *  address) before creating it. */
export function previewCaveHost(name: string, slug = ""): Promise<CaveHostPreview> {
  const params = new URLSearchParams({ name });
  if (slug) params.set("slug", slug);
  return getJSON<CaveHostPreview>(`/api/v1/caves/host-preview?${params}`);
}

/** Super-admin: permanently delete a PRISTINE cave and its children (ADR 0059).
 *  Throws an ApiError carrying the server message on a 409 — the cave has
 *  activity (a sold ticket or a run event) and must be archived instead (ADR 0067). */
export function deleteCave(id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("DELETE", `/api/v1/caves/${encodeURIComponent(id)}`).then(() => undefined);
}

/** Super-admin: retire a cave (ADR 0067) — hide it from the normal views while
 *  keeping its rows + history. The retire path for a cave that has activity. */
export function archiveCave(id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("POST", `/api/v1/caves/${encodeURIComponent(id)}/archive`, {}).then(() => undefined);
}

/** Super-admin: restore a retired cave to the normal views (ADR 0067). */
export function unarchiveCave(id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("POST", `/api/v1/caves/${encodeURIComponent(id)}/unarchive`, {}).then(() => undefined);
}

export function fetchCave(id: string): Promise<Cave> {
  return getJSON<{ cave: Cave }>(`/api/v1/caves/${encodeURIComponent(id)}`).then((b) => b.cave);
}

export function fetchCaveEvents(id: string, upcoming?: boolean): Promise<CaveEvent[]> {
  const q = upcoming ? "?upcoming=1" : "";
  return getJSON<{ events: CaveEvent[] }>(
    `/api/v1/caves/${encodeURIComponent(id)}/events${q}`,
  ).then((b) => b.events);
}

export function createCaveEvent(
  id: string,
  body: NewCaveEvent,
): Promise<{ events: CaveEvent[]; seriesId: string; warning?: string }> {
  // `warning` is a non-blocking advisory (e.g. an overlapping CaveCast, CF-5/LSC-372).
  return sendJSON<{ events: CaveEvent[]; seriesId: string; warning?: string }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(id)}/events`,
    body,
  );
}

export async function deleteCaveEvent(id: string, eventId: string): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "DELETE",
    `/api/v1/caves/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}`,
  );
}

/** Rename an event in place (LSC-572 / ADR 0251) — id + relationships preserved. */
export function renameCaveEvent(id: string, eventId: string, title: string): Promise<{ event: CaveEvent }> {
  return sendJSON<{ event: CaveEvent }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}`,
    { title },
  );
}

/** Set a cave's event display order from the ordered id list (LSC-572 / ADR 0251). */
export async function reorderCaveEvents(id: string, order: string[]): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(id)}/events/order`,
    { order },
  );
}

export function fetchCaveTicketTypes(id: string): Promise<TicketType[]> {
  return getJSON<{ ticketTypes: TicketType[] }>(
    `/api/v1/caves/${encodeURIComponent(id)}/ticket-types`,
  ).then((b) => b.ticketTypes);
}

export function createCaveTicketType(id: string, body: NewTicketType): Promise<TicketType> {
  return sendJSON<{ ticketType: TicketType }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(id)}/ticket-types`,
    body,
  ).then((b) => b.ticketType);
}

/** Rename a ticket type in place (LSC-572 / ADR 0251) — id + relationships preserved. */
export async function renameCaveTicketType(id: string, ttId: string, name: string): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(id)}/ticket-types/${encodeURIComponent(ttId)}`,
    { name },
  );
}

/** Set a cave's ticket-type display order from the ordered id list (LSC-572 / ADR 0251). */
export async function reorderCaveTicketTypes(id: string, order: string[]): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(id)}/ticket-types/order`,
    { order },
  );
}

export async function deleteCaveTicketType(id: string, ttId: string): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "DELETE",
    `/api/v1/caves/${encodeURIComponent(id)}/ticket-types/${encodeURIComponent(ttId)}`,
  );
}

export function fetchCaveAccess(id: string): Promise<{ cave: Cave; access: CaveAccessRow[]; canDelegatePublish: boolean }> {
  return getJSON<{ cave: Cave; access: CaveAccessRow[]; canDelegatePublish: boolean }>(
    `/api/v1/caves/${encodeURIComponent(id)}/access`,
  );
}

export async function setCaveAccess(id: string, username: string, allow: boolean): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", `/api/v1/caves/${encodeURIComponent(id)}/access`, {
    username,
    allow,
  });
}

/** Grant/revoke a user's per-cave lobby-publish delegation (LSC-260). Admin-publisher only. */
export async function setLobbyPublishAccess(id: string, username: string, allow: boolean): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", `/api/v1/caves/${encodeURIComponent(id)}/lobby/publish-access`, {
    username,
    allow,
  });
}

/** Grant/revoke a user's event-scoped production access (ADR 0038). */
export async function setEventAccess(
  caveId: string,
  eventId: string,
  username: string,
  allow: boolean,
): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/access`,
    { username, allow },
  );
}

// ── recording opt-out hierarchy (ADR 0080) ───────────────────────────────────
// Recording is ON by default for every CaveCast; the effective value is
// event ?? cave ?? platform-admin ?? on (lowest override wins).

/** Read the platform-wide recording default (super-admin). */
export async function getPlatformRecordingDefault(): Promise<boolean> {
  const r = await getJSON<{ enabled: boolean }>("/api/v1/admin/recording-default");
  return r.enabled;
}

/** Set the platform-wide recording default (super-admin). */
export async function setPlatformRecordingDefault(enabled: boolean): Promise<void> {
  await sendJSON<{ enabled: boolean }>("PUT", "/api/v1/admin/recording-default", { enabled });
}

/** Set/clear the cave-level recording opt-out (null = inherit platform). */
export async function setCaveRecordDefault(caveId: string, record: boolean | null): Promise<void> {
  await sendJSON<{ recordDefault: boolean | null }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/recording-default`,
    { record },
  );
}

/** Set/clear the event-level recording opt-out (null = inherit cave). */
export async function setEventRecord(caveId: string, eventId: string, record: boolean | null): Promise<void> {
  await sendJSON<{ record: boolean | null }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/recording`,
    { record },
  );
}

/** Set the event-level Ask-the-Legend default (ADR 0093): true disables Ask for this
 *  event's shows (seeds program.askDisabled at go-live). */
export async function setEventAsk(caveId: string, eventId: string, disabled: boolean): Promise<void> {
  await sendJSON<{ askDisabled: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/ask`,
    { disabled },
  );
}

/** Event-level live-delivery choice (ADR 0140): "" inherits the platform default,
 *  "webrtc" is low-latency (interactive), "hls" is segmented (AirPlay/PiP/Cast). */
export type Delivery = "" | "webrtc" | "hls";

/** Set the event-level delivery mode. 409 while the broadcast is live/stopped. */
export async function setEventDelivery(caveId: string, eventId: string, delivery: Delivery): Promise<void> {
  await sendJSON<{ delivery: Delivery }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/delivery`,
    { delivery },
  );
}

/** Set/clear the event-level private-rehearsal flag (ADR 0259): true hides the event
 *  from every public read + admission, yet it stays producible for crew. */
export async function setEventRehearsal(caveId: string, eventId: string, rehearsal: boolean): Promise<void> {
  await sendJSON<{ rehearsal: boolean }>(
    "PUT",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/rehearsal`,
    { rehearsal },
  );
}

// ── live broadcast (ADR 0011 — Phase 0) ──────────────────────────────────────

/** A live session bound to an event; its LiveKit room is derived from the id. */
export interface Broadcast {
  id: string;
  eventId: string;
  caveId: string;
  /** LiveKit room name (e.g. bcast_<id>). */
  room: string;
  /** idle before anyone joins, live once a publisher token is minted, stopped
   *  when paused into a recoverable standby (ADR 0039), ended after. */
  status: "idle" | "live" | "stopped" | "ended";
  /** Private dry run (ADR 0244, LSC-507): not public, no fan notification, no
   *  recording/egress, never consumes the event. Drives the console banner. */
  rehearsal?: boolean;
}

/** What a publisher needs to join the room: a LiveKit token + SFU ws URL. */
export interface BroadcastToken {
  token: string;
  /** ws:// SFU URL the browser connects to. */
  url: string;
  room: string;
  identity: string;
  broadcast: Broadcast;
}

/**
 * Get-or-create the broadcast for an event (idempotent; gated by cave-config on
 * the event's cave, so only reachable where the caller already has access).
 */
export function ensureBroadcast(caveId: string, eventId: string): Promise<Broadcast> {
  return sendJSON<{ broadcast: Broadcast }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/broadcast`,
  ).then((b) => b.broadcast);
}

/** Mint a LiveKit publisher join token and mark the broadcast live. The Director
 *  console passes `as: "director"` so its connection takes a reserved identity — its
 *  ISO capture (the private talkback mic only, ADR 0213) is then excluded from the VOD
 *  composite (ADR 0225, LSC-471). The Legend Go-Live omits it and stays composited. */
export function broadcastToken(
  broadcastId: string,
  opts?: { as?: "director"; rehearsal?: boolean },
): Promise<BroadcastToken> {
  const params = new URLSearchParams();
  if (opts?.as) params.set("as", opts.as);
  if (opts?.rehearsal) params.set("rehearsal", "1"); // private dry run (ADR 0244, LSC-507)
  const qs = params.toString();
  return sendJSON<BroadcastToken>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/token${qs ? `?${qs}` : ""}`,
  );
}

/** Read a broadcast's current state. */
export function fetchBroadcast(broadcastId: string): Promise<Broadcast> {
  return getJSON<{ broadcast: Broadcast }>(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}`,
  ).then((b) => b.broadcast);
}

/** Stop a broadcast into a recoverable standby pause (ADR 0039) — director or
 *  legend. Marks it stopped and persists a standby state so viewers see a
 *  "Standby — paused" slate, not the end slate; going live again resumes. */
export function stopBroadcast(broadcastId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/stop`,
  ).then(() => undefined);
}

/** End a broadcast (ADR 0025) — director or legend. Marks it ended and persists
 *  the end state so late joiners see the end slate. */
export function endBroadcast(broadcastId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/end`,
  ).then(() => undefined);
}

// ── production hold: server-authoritative mute/pause (ADR 0055) ──────────────
// Each endpoint does BOTH: an authoritative LiveKit SFU track mute on the
// on-program publisher AND set+persist the program flag (so viewers reflect
// instantly + late-joiners get it). Production-gated (director or legend). It
// returns the updated program state.

/** Authoritatively mute the on-program audio + set audioMuted (ADR 0055). */
export function muteBroadcast(broadcastId: string): Promise<ProgramState> {
  return sendJSON<{ program: ProgramState }>("POST", `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/mute`).then((b) => b.program);
}

/** Unmute the on-program audio + clear audioMuted (ADR 0055). */
export function unmuteBroadcast(broadcastId: string): Promise<ProgramState> {
  return sendJSON<{ program: ProgramState }>("POST", `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/unmute`).then((b) => b.program);
}

/** Authoritatively pause the on-program video + set videoPaused (pops the hold
 *  playlist). An optional resumeSeconds arms a timed auto-resume (ADR 0055). */
export function pauseBroadcast(broadcastId: string, resumeSeconds?: number): Promise<ProgramState> {
  return sendJSON<{ program: ProgramState }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/pause`,
    resumeSeconds ? { resumeSeconds } : undefined,
  ).then((b) => b.program);
}

/** Resume the on-program video + clear videoPaused (ADR 0055). */
export function resumeBroadcast(broadcastId: string): Promise<ProgramState> {
  return sendJSON<{ program: ProgramState }>("POST", `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/resume`).then((b) => b.program);
}

// ── guest publishers (ADR 0044) ──────────────────────────────────────────────
// A single-broadcast invite lets a NON-operator guest publish into one room. The
// operator mints/lists/revokes invites (production-gated); the guest-join call is
// PUBLIC and returns a room-scoped LiveKit publisher token (no app authority).

/** One guest-publisher invite as shown on the Director console. */
export interface GuestInvite {
  code: string;
  label: string;
  /** Shareable public join link — <public base>/guest/<code>. */
  joinUrl: string;
  expiresAt: string;
  revoked: boolean;
  used: boolean;
  createdAt: string;
}

/** What a guest needs to publish: the same shape as an operator's token, minus
 *  any cave/app context — a LiveKit token + SFU ws URL scoped to one room. */
export interface GuestJoinToken {
  mode: "webrtc" | "hls";
  url: string;
  token: string;
  room: string;
  identity: string;
  /** The display name the guest publishes under. */
  label: string;
  /** The STABLE program key (participant Name) the Director assigns to a slot
   *  (ADR 0042). The guest can't derive it from its unique identity, so it's
   *  returned here to resolve which dueling slot (if any) the guest is on and
   *  which board to draw (ADR 0056). */
  name?: string;
  /** The CURRENT program state, so a (re)joining guest re-syncs its dueling board
   *  from state instead of a stale/blank one until the next relay (ADR 0062). */
  program?: ProgramState;
}

/** Mint a guest-join invite for a broadcast (operator). Returns the shareable
 *  link the operator hands to the guest. */
export function createGuestInvite(
  broadcastId: string,
  label: string,
  ttlMinutes?: number,
): Promise<{ code: string; joinUrl: string; expiresAt: string }> {
  return sendJSON(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/guest-invites`,
    ttlMinutes ? { label, ttlMinutes } : { label },
  );
}

/** List a broadcast's guest invites (operator). */
export function listGuestInvites(broadcastId: string): Promise<GuestInvite[]> {
  return getJSON<{ invites: GuestInvite[] }>(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/guest-invites`,
  ).then((b) => b.invites);
}

/** Revoke a guest invite (operator). */
export function revokeGuestInvite(broadcastId: string, code: string): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/guest-invites/${encodeURIComponent(code)}/revoke`,
  ).then(() => undefined);
}

/** Redeem a guest invite code and mint a room-scoped publisher token. PUBLIC —
 *  no session; the code is the sole credential. */
export function guestJoin(code: string, displayName?: string): Promise<GuestJoinToken> {
  return sendJSON<GuestJoinToken>(
    "POST",
    `/api/v1/broadcasts/guest/${encodeURIComponent(code)}/join`,
    displayName ? { displayName } : {},
  );
}

/** Relay a guest's telestration strokes for their OWN dueling slot (ADR 0056).
 *  PUBLIC — authenticated only by the invite code. A guest holds a media-only
 *  LiveKit token (data-publish denied, ADR 0044), so it cannot relay strokes
 *  itself; the SERVER merges them into only the guest's slot and fans them out.
 *  Returns the slot the strokes landed on. */
export function guestBoard(
  code: string,
  strokes: WhiteboardStroke[],
): Promise<{ ok: boolean; slot: DuelSlot }> {
  return sendJSON<{ ok: boolean; slot: DuelSlot }>(
    "POST",
    `/api/v1/broadcasts/guest/${encodeURIComponent(code)}/board`,
    { strokes },
  );
}

// ── whiteboard from device (ADR 0095) ────────────────────────────────────────
// The Legend/Director mints a one-time code + QR-able URL; a touch device draws on
// the SHARED whiteboard. The device holds no token/session — the code (kind
// 'whiteboard', which never mints a media token) authenticates the two public
// endpoints, and the SERVER merges the strokes into program.overlay.strokes.

/** Legend/Director: mint a one-time Whiteboard-from-Device code (production-gated). */
export function createWhiteboardDevice(broadcastId: string): Promise<{ code: string; joinUrl: string; expiresAt: string }> {
  return sendJSON<{ code: string; joinUrl: string; expiresAt: string }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/whiteboard-device`,
    {},
  );
}

/** Device page: read the current program (slate template + strokes) by code. PUBLIC. */
export function fetchWhiteboardDeviceState(code: string): Promise<ProgramState> {
  return getJSON<{ program: ProgramState }>(
    `/api/v1/broadcasts/whiteboard/${encodeURIComponent(code)}`,
  ).then((r) => r.program);
}

/** Device page: relay the device's strokes onto the shared whiteboard. PUBLIC —
 *  authenticated only by the code; the server merges ONLY overlay.strokes (409 when
 *  no whiteboard is on program). */
export function postWhiteboardDeviceStrokes(code: string, strokes: WhiteboardStroke[]): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/broadcasts/whiteboard/${encodeURIComponent(code)}/strokes`,
    { strokes },
  ).then(() => undefined);
}

/** Device page: change the shared whiteboard's slate TEMPLATE from the device (ADR
 *  0119). PUBLIC — same code-authed merge endpoint; the server sets ONLY overlay.board
 *  (409 when no whiteboard is on program), preserving strokes/layout/everything else. */
export function postWhiteboardDeviceBoard(code: string, board: BoardId): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/broadcasts/whiteboard/${encodeURIComponent(code)}/strokes`,
    { board },
  ).then(() => undefined);
}

// ── live chat, relayed through the API (ADR 0026) ─────────────────────────────
// Replaces the LiveKit-data path so viewer→publisher chat works, and so chat
// survives the HLS delivery mode (viewers not in the room).

export interface ChatWireMessage {
  id: number;
  userId: number;
  user: string;
  text: string;
  question: boolean;
}

/** Post a chat message; the server stamps identity and returns it with its id. */
export function postChat(broadcastId: string, text: string, question: boolean): Promise<ChatWireMessage> {
  return sendJSON<{ message: ChatWireMessage }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/chat`,
    { text, question },
  ).then((b) => b.message);
}

/** Post to the private Legend↔Director production chat (LSC-404, producers only). */
export function postProductionChat(broadcastId: string, text: string): Promise<ChatWireMessage> {
  return sendJSON<{ message: ChatWireMessage }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/production-chat`,
    { text },
  ).then((b) => b.message);
}

/** Long-poll the private production chat after `since` (producers only). */
export async function pollProductionChat(
  broadcastId: string,
  since: number,
  signal?: AbortSignal,
): Promise<{ messages: ChatWireMessage[]; cursor: number }> {
  const resp = await apiFetch(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/production-chat?since=${since}`,
    { signal },
  );
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as { messages: ChatWireMessage[]; cursor: number };
}

/** Long-poll for chat messages after `since` (the last id seen). */
export async function pollChat(
  broadcastId: string,
  since: number,
  signal?: AbortSignal,
): Promise<{ messages: ChatWireMessage[]; cursor: number }> {
  const resp = await apiFetch(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/chat?since=${since}`,
    { signal },
  );
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as { messages: ChatWireMessage[]; cursor: number };
}

export interface SentFanQuestion {
  /** Monotonic approval/delivery cursor. */
  id: number;
  /** Original chat_messages id. */
  messageId: number;
  fan: string;
  text: string;
  sentAt: string;
}

/** Director/operator durably sends one existing Fan question to the Legend. */
export function sendFanQuestion(broadcastId: string, messageId: number): Promise<SentFanQuestion> {
  return sendJSON<{ question: SentFanQuestion }>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/questions/${messageId}/send`,
  ).then((b) => b.question);
}

/** Long-poll the durable Director-approved question stream. */
export async function pollSentFanQuestions(
  broadcastId: string,
  since: number,
  signal?: AbortSignal,
): Promise<{ questions: SentFanQuestion[]; cursor: number }> {
  const resp = await apiFetch(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/questions/sent?since=${since}`,
    { signal },
  );
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as { questions: SentFanQuestion[]; cursor: number };
}

/**
 * The Legend's current question cue (ADR 0046): the most-recent approved,
 * not-yet-acknowledged question (null when the queue is clear) plus the count of
 * pending (approved, unacknowledged) questions.
 */
export interface QuestionCue {
  question: SentFanQuestion | null;
  pending: number;
}

/**
 * Long-poll the Legend's current question cue. `since` is the id of the cue the
 * client currently shows (0 when none); the server returns as soon as the cue
 * changes — a newly approved question, or the current one acknowledged.
 */
export async function pollQuestionCue(
  broadcastId: string,
  since: number,
  signal?: AbortSignal,
): Promise<QuestionCue> {
  const resp = await apiFetch(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/questions/cue?since=${since}`,
    { signal },
  );
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as QuestionCue;
}

/** Acknowledge (mark handled) a sent question; returns the advanced cue. */
export function ackFanQuestion(broadcastId: string, sentId: number): Promise<QuestionCue> {
  return sendJSON<QuestionCue>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/questions/${sentId}/ack`,
  );
}

// ── director program state + ticket-gated watch (ADR 0012 — Phases 2/3) ───────

/** One freehand whiteboard stroke; points are normalized 0..1 to the surface. */
export interface WhiteboardPoint {
  x: number;
  y: number;
}
export interface WhiteboardStroke {
  color: string;
  width: number;
  points: WhiteboardPoint[];
}
/** How the whiteboard composes with the legend feed (ADR 0022):
 *  - full: whiteboard fills, legend hidden
 *  - pip-legend: whiteboard fills, legend in a small corner box (default)
 *  - pip-board: legend fills, whiteboard in a small corner box
 *  - side: side-by-side (legend | whiteboard) */
export type WhiteboardLayout = "full" | "pip-legend" | "pip-board" | "side";
/** A preconfigured board rendered behind the whiteboard strokes (ADR 0045,
 *  extended by ADR 0057): a blank slate, a football gridiron, a basketball court,
 *  a soccer pitch, or a hockey rink. Vector-drawn on the canvas (no image assets);
 *  the Director picks it, the Legend draws on it. */
export type BoardTemplate = "blank" | "football" | "basketball" | "soccer" | "hockey";
/** A whiteboard board value: either a built-in vector template (BoardTemplate) OR
 *  the id of an admin-uploaded image slate (ADR 0058). The two coexist in every
 *  picker; a client resolves an uploaded id to its image via fetchPublicBoardSlates.
 *  Relayed the same opaque way as any other program-state field. */
export type BoardId = string;
/** A branded sponsor slate shown full-frame during a break. */
export interface AdSlate {
  brand: string;
  offer: string;
  tier: string;
}

/** The director's shared program state, composited client-side by viewers
 *  (ADR 0012/0022). Whiteboard carries a real drawing; Ad carries the break to
 *  play full-frame (a video clip or a sponsor slate). */
export interface ProgramOverlay {
  kind: "none" | "whiteboard" | "ad" | "lower-third";
  /** Whiteboard: optional caption + the drawing + how it composes with the feed. */
  text?: string;
  strokes?: WhiteboardStroke[];
  layout?: WhiteboardLayout;
  /** Which board renders behind the strokes: a built-in vector template (ADR 0045)
   *  OR an admin-uploaded slate id (ADR 0058). Absent = "blank" (back-compatible
   *  with pre-0045 program state). */
  board?: BoardId;
  /** Ad: the break to play full-frame. */
  adId?: string;
  adName?: string;
  adVideoUrl?: string;
  adImageUrl?: string;
  adSlate?: AdSlate;
  /** Lower third: a name/title band over the live feed (ADR 0025). */
  title?: string;
  subtitle?: string;
  /** Lower-third STYLE (ADR 0088, opaque + additive) — the accent bar/keyline colour
   *  and the band position; the fan renders them. Absent = yellow accent, left. */
  accent?: string;
  position?: "left" | "center";
}
/** Program layout (ADR 0054, extended for 3/4-up in ADR 0212): how the on-program
 *  sources compose for viewers. Absent/"single" = one source full-frame (today's
 *  behavior, backward-compatible). Every multi-head layout renders client-side (no
 *  server-side egress compositing, consistent with ADR 0044 switch-not-compose):
 *   - dual        — two heads side by side.
 *   - dual-board  — two heads + the SHARED whiteboard (the ADR 0045 whiteboard
 *                   overlay board+strokes), rendered read-only for fans.
 *   - dueling     — a 2×2: each head paired with its OWN board (per-slot boards).
 *   - tri         — 3-up "Legend large": slot A dominant, B/C stacked beside it.
 *   - tri-equal   — three balanced heads.
 *   - quad        — 4-up "Legend large": slot A dominant, B/C/D stacked beside it.
 *   - quad-equal  — four heads in a 2×2 grid.
 *   - board-tri   — board dominant + three small PiP heads (shared board).
 *   - board-quad  — board dominant + four small PiP heads (shared board).
 *  The extra heads use `sourceC`/`sourceD`; board-tri/board-quad reuse the SHARED
 *  whiteboard region (like dual-board), NOT per-slot boards. */
export type ProgramLayout =
  | "single"
  | "dual"
  | "dual-board"
  | "dueling"
  | "tri"
  | "tri-equal"
  | "quad"
  | "quad-equal"
  | "board-tri"
  | "board-quad";

/** A dueling slot (ADR 0054): "a" is the slot-A publisher (`source`), "b" is the
 *  slot-B publisher (`sourceB`). Only the `dueling` layout has PER-SLOT boards, so
 *  this stays a/b (the board map is keyed by it). */
export type DuelSlot = "a" | "b";

/** A head/source slot in a multi-head composition (ADR 0054/0212): "a" = `source`,
 *  "b" = `sourceB`, "c" = `sourceC`, "d" = `sourceD`. Which slots a layout actually
 *  uses is `layoutSlots(layout)` in programLayout.tsx. */
export type SourceSlot = "a" | "b" | "c" | "d";

/** One publisher's own telestration board in the `dueling` layout (ADR 0054) —
 *  the vector template (ADR 0045) plus that publisher's normalized strokes. */
export interface DuelBoard {
  board?: BoardId;
  strokes?: WhiteboardStroke[];
}

/** The four transition styles a TAKE can carry (ADR 0081/0082). A `cut` is
 *  instant (no animation); the rest are timed, animated CLIENT-SIDE by the fan. */
export type TransitionType = "cut" | "dissolve" | "wipe" | "slide";

/** A transition hint (ADR 0082) — an opaque, ADDITIVE field the Director attaches
 *  to a TAKE so the fan can animate the program swap (crossfade / wipe / slide).
 *  Animation-only: late-joiners just get the final program. `ms` is the duration. */
export interface Transition {
  type: TransitionType;
  ms: number;
}

export interface ProgramState {
  /** Participant identity currently on program ("" / undefined = none). Slot A in
   *  a multi-head layout (ADR 0054). */
  source?: string;
  /** Slot B's on-program publisher STABLE key (ADR 0054) — the second head in a
   *  multi-head layout; unused/ignored when `layout` is absent/"single". */
  sourceB?: string;
  /** Slot C's on-program publisher STABLE key (ADR 0212) — the third head in a
   *  3-up/4-up/board-dominant layout; unused when the layout has fewer than 3 slots. */
  sourceC?: string;
  /** Slot D's on-program publisher STABLE key (ADR 0212) — the fourth head in a
   *  4-up/board-quad layout; unused when the layout has fewer than 4 slots. */
  sourceD?: string;
  /** How the sources compose for viewers (ADR 0054). Absent = "single". */
  layout?: ProgramLayout;
  /** Per-slot telestration boards for the `dueling` layout (ADR 0054): each slot's
   *  assigned publisher draws only their OWN board, keyed by slot ("a"/"b"). Only
   *  meaningful when `layout === "dueling"`; `dual-board` uses the shared
   *  whiteboard overlay and single-layout telestration is unchanged (ADR 0045). */
  boards?: Partial<Record<DuelSlot, DuelBoard>>;
  overlay?: ProgramOverlay;
  /** The broadcast is paused in a recoverable standby (ADR 0039) — viewers show
   *  the standby slate (distinct from the terminal end slate). */
  standby?: boolean;
  /** The broadcast has ended (ADR 0025) — viewers show the end slate. */
  ended?: boolean;
  /** Production hold (ADR 0053/0055) — a program hold set by the legend (or the
   *  director on their behalf). Relayed like any other program state AND enforced
   *  authoritatively at the SFU by the /mute·/pause endpoints (ADR 0055). Independent
   *  flags: mute program audio, and/or pause program video (which pops the rotating
   *  hold playlist over the stage). */
  audioMuted?: boolean;
  videoPaused?: boolean;
  /** Timed auto-resume (ADR 0055): epoch-ms at which the hold auto-clears. Clients
   *  treat the hold as inactive once now >= resumeAt (so it clears with no further
   *  action); the server reaper clears the flags + unmutes the SFU track. Absent =
   *  hold until an operator resumes. */
  resumeAt?: number;
  /** Chroma-key background selection (ADR 0070). The Legend's client composites its
   *  green-screen camera over the selected background BEFORE publishing, so the
   *  keyed result is baked into the Legend feed. Rides the opaque program state so
   *  both the Legend and the Director (as an override) can set it. */
  chroma?: ChromaState;
  /** Ask-the-Legend is ON by default; set true to turn the fan question submit panel
   *  OFF for this CaveCast (ADR 0077). Either the Director or the Legend may toggle
   *  it; when true the Fan watch surface hides the Ask-the-Legend rail. Opaque
   *  program-state field — no route/schema change. */
  askDisabled?: boolean;
  /** Optional custom "Broadcast Ended" board (ADR 0104): a resolved image URL (the
   *  operator picks/uploads it via the ad/slate library) shown full-frame on the Fan
   *  end slate instead of the default "That's a wrap" card. Opaque program-state
   *  field — relayed + persisted like any program state, no route/schema change. */
  endedBoard?: string;
  /** Transition hint for THIS take (ADR 0081/0082). When the Director stages a new
   *  scene in preview and TAKEs it with AUTO / a full T-bar throw, the take carries
   *  the transition style + duration so the fan animates the program swap
   *  client-side (crossfade / wipe / slide). Absent or `{type:"cut"}` = an instant
   *  cut (today's behavior). Opaque + additive — animation-only, no route/schema
   *  change; a late-joiner simply gets the final program with no animation. */
  transition?: Transition;
  /** Fade-to-black (ADR 0081/0082): the Director has blacked out PROGRAM. Fans paint
   *  an opaque black layer over the live composition + program overlays (it does NOT
   *  hide the lifecycle slates — standby/ended still show). Cleared by taking the
   *  program back up. Opaque + additive. */
  blackout?: boolean;
  /** Live-to-fans (ADR 0148 / iOS ADR 0014, LSC-268): set true by the Director's
   *  TAKE — "live begins at the director's TAKE", not at the legend's publish.
   *  Absent/false = pre-show (fans hold on the pre-start slate no matter what source
   *  is merely STAGED). Written only by `applyScene` (the TAKE path); cleared for free
   *  by the fresh-go-live program reset ({}). Opaque + additive: a client that ignores
   *  it degrades to the old video-frame heuristic rather than blacking out. */
  live?: boolean;
}

/** Background compositing selection + params (ADR 0070/0071). */
export interface ChromaState {
  /** Whether the Legend should composite over the background. */
  enabled?: boolean;
  /** Compositing mode (ADR 0071): "chroma" = green-screen key (default), "ml" =
   *  MediaPipe person segmentation (no green screen). */
  mode?: "chroma" | "ml";
  /** Selected background id from the cave library (resolve → imageUrl via the list). */
  backgroundId?: string;
  /** Key color hex (default "#00b140" green), and soft-key tuning (0..1). */
  color?: string;
  similarity?: number;
  smoothness?: number;
  spill?: number;
  /** How the background fills the camera frame (default "cover"). */
  fit?: "cover" | "contain" | "stretch";
  /** Who last set it, so the UI can show a Director override. */
  by?: "legend" | "director";
}

// ── ad library (ADR 0022) ─────────────────────────────────────────────────────

/** One item in a cave's ad library. */
export interface Ad {
  id: string;
  name: string;
  kind: "slate" | "video";
  videoUrl: string;
  imageUrl: string;
  brand: string;
  offer: string;
  tier: string;
}

/** Director: a cave's ad library. */
export function fetchCaveAds(caveId: string): Promise<Ad[]> {
  return getJSON<{ ads: Ad[] }>(`/api/v1/caves/${encodeURIComponent(caveId)}/ads`).then((b) => b.ads ?? []);
}

/** Director: add a branded slate ad to the library. */
export function createSlateAd(
  caveId: string,
  slate: { name: string; brand: string; offer: string; tier: string },
): Promise<Ad> {
  return sendJSON<{ ad: Ad }>("POST", `/api/v1/caves/${encodeURIComponent(caveId)}/ads`, slate).then((b) => b.ad);
}

/** Director: upload a video clip as an ad (multipart). */
export async function uploadVideoAd(caveId: string, file: File, name?: string): Promise<Ad> {
  return uploadAdAsset(caveId, "video", file, name);
}

/** Director: upload a slate image (a pre-made sponsor graphic) as an ad (ADR 0025). */
export async function uploadSlateAd(caveId: string, file: File, name?: string): Promise<Ad> {
  return uploadAdAsset(caveId, "image", file, name);
}

async function uploadAdAsset(caveId: string, field: "video" | "image", file: File, name?: string): Promise<Ad> {
  const form = new FormData();
  form.append(field, file);
  if (name) form.append("name", name);
  const resp = await apiFetch(`/api/v1/caves/${encodeURIComponent(caveId)}/ads/upload`, {
    method: "POST",
    body: form,
  });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { ad: Ad }).ad;
}

// ── chroma-key background library (ADR 0070) ─────────────────────────────────

/** One background in a cave's chroma-key library — what a client needs to composite. */
export interface Background {
  id: string;
  name: string;
  /** Served /uploads/backgrounds/... image path. */
  imageUrl: string;
}

/** Operator: a cave's chroma-key background library (newest first). */
export function fetchCaveBackgrounds(caveId: string): Promise<Background[]> {
  return getJSON<{ backgrounds: Background[] }>(
    `/api/v1/caves/${encodeURIComponent(caveId)}/backgrounds`,
  ).then((b) => b.backgrounds ?? []);
}

/** Operator: upload a green-screen background image to the cave's library (multipart). */
export async function uploadBackground(caveId: string, file: File, name?: string): Promise<Background> {
  const form = new FormData();
  form.append("image", file);
  if (name) form.append("name", name);
  const resp = await apiFetch(`/api/v1/caves/${encodeURIComponent(caveId)}/backgrounds/upload`, {
    method: "POST",
    body: form,
  });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { background: Background }).background;
}

/** Operator: remove a background from the cave's library. */
export function deleteBackground(caveId: string, id: string): Promise<void> {
  return apiFetch(
    `/api/v1/caves/${encodeURIComponent(caveId)}/backgrounds/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  ).then((resp) => {
    if (!resp.ok) throw new ApiError(resp.status, `delete failed (${resp.status})`);
  });
}

// ── admin-uploaded board slates (ADR 0058) ───────────────────────────────────
// A GLOBAL, platform-wide catalog of custom whiteboard board backgrounds. A
// super-admin uploads image slates that appear in every board picker alongside
// the built-in vector courts (ADR 0045/0057). A whiteboard `board` value carries
// a slate's id; any client maps it to the image url via fetchPublicBoardSlates.

/** One admin-uploaded board slate — only what a client needs to map id → image. */
export interface BoardSlate {
  id: string;
  label: string;
  /** Served /uploads/slates/... image path. */
  url: string;
}

/** Public: the board-slate catalog (any whiteboard client resolves board ids here). */
export function fetchPublicBoardSlates(): Promise<BoardSlate[]> {
  return getJSON<{ slates: BoardSlate[] }>("/api/v1/public/board-slates").then((b) => b.slates ?? []);
}

/** Super-admin: the board-slate catalog for management. */
export function listBoardSlates(): Promise<BoardSlate[]> {
  return getJSON<{ slates: BoardSlate[] }>("/api/v1/admin/board-slates").then((b) => b.slates ?? []);
}

/** Super-admin: upload a custom board-slate image + label (multipart). */
export async function uploadBoardSlate(file: File, label: string): Promise<BoardSlate> {
  const form = new FormData();
  form.append("image", file);
  if (label) form.append("label", label);
  const resp = await apiFetch("/api/v1/admin/board-slates/upload", { method: "POST", body: form });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { slate: BoardSlate }).slate;
}

/** Super-admin: delete a board slate. */
export function deleteBoardSlate(id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("DELETE", `/api/v1/admin/board-slates/${encodeURIComponent(id)}`).then(() => undefined);
}

// ── pre/post-roll looping playlists (ADR 0052) ────────────────────────────────

/** Which looping reel an item belongs to: the hype reel before (pre), the thanks
 *  reel after (post), or the rotating paused-video hold slate playlist (hold,
 *  ADR 0055 — replaced ADR 0053's single hold image). */
export type RollName = "pre" | "post" | "hold";

/** One clip in a pre/post-roll playlist — an image slate or a video, served from
 *  /uploads (mirrors the ad content model). */
export interface RollItem {
  id: string;
  scope: "global" | "cave";
  roll: RollName;
  kind: "image" | "video";
  url: string;
  title: string;
  position: number;
  /** Image dwell in ms; 0 for a video = play the full clip. */
  durationMs: number;
  locked: boolean;
  /** Per-cave listing only: whether the item is a global or the cave's own,
   *  whether this cave may hide it (every own item; unlocked globals) + its current
   *  hidden state. The listing returns visible items first in playback order, then
   *  the hidden ones (ADR 0254). */
  source?: "global" | "cave";
  removable?: boolean;
  suppressed?: boolean;
}

/** The {pre, post, hold} set returned by every roll read. `hold` is the rotating
 *  paused-video slate playlist (ADR 0055). */
export interface RollPlaylists {
  pre: RollItem[];
  post: RollItem[];
  hold: RollItem[];
}

/** The cave console's resolved view: the playlists plus the contribution flag. */
export interface CaveRollPlaylists extends RollPlaylists {
  allowContrib: boolean;
}

/** A create body for a roll item added by url (image slate or video). */
export interface NewRollItem {
  roll: RollName;
  kind: "image" | "video";
  url: string;
  title?: string;
  position?: number;
  durationMs?: number;
  locked?: boolean;
}

// -- global playlist (super-admin) --

/** Super-admin: the global pre/post-roll playlists. */
export function fetchGlobalRollItems(): Promise<RollPlaylists> {
  return getJSON<RollPlaylists>("/api/v1/admin/roll-items");
}

/** Super-admin: add a global roll item by url. */
export function createGlobalRollItem(item: NewRollItem): Promise<RollItem> {
  return sendJSON<{ item: RollItem }>("POST", "/api/v1/admin/roll-items", item).then((b) => b.item);
}

/** Super-admin: delete a global roll item. */
export function deleteGlobalRollItem(id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("DELETE", `/api/v1/admin/roll-items/${encodeURIComponent(id)}`).then(() => undefined);
}

/** Super-admin: lock/unlock a global roll item (locked = caves cannot remove it). */
export function setRollItemLocked(id: string, locked: boolean): Promise<void> {
  return sendJSON<{ ok: boolean }>("PUT", `/api/v1/admin/roll-items/${encodeURIComponent(id)}/lock`, { locked }).then(() => undefined);
}

/** Super-admin: toggle a cave's permission to add its own roll items. */
export function setCaveRollContrib(caveId: string, allow: boolean): Promise<void> {
  return sendJSON<{ ok: boolean }>("PUT", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-contrib`, { allow }).then(() => undefined);
}

/** Super-admin: upload an image/video global roll asset + create the item. */
export function uploadGlobalRollItem(
  field: "image" | "video",
  file: File,
  meta: { roll: RollName; title?: string; position?: number; durationMs?: number },
): Promise<RollItem> {
  return uploadRollAsset("/api/v1/admin/roll-items/upload", field, file, meta);
}

// -- cave operator (needs allow_roll_contrib) --

/** Operator: the cave's resolved pre/post-roll (globals + own) with flags. */
export function fetchCaveRollItems(caveId: string): Promise<CaveRollPlaylists> {
  return getJSON<CaveRollPlaylists>(`/api/v1/caves/${encodeURIComponent(caveId)}/roll-items`);
}

/** Operator: add the cave's own roll item by url. */
export function createCaveRollItem(caveId: string, item: NewRollItem): Promise<RollItem> {
  return sendJSON<{ item: RollItem }>("POST", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-items`, item).then((b) => b.item);
}

/** Operator: delete one of the cave's own roll items. */
export function deleteCaveRollItem(caveId: string, id: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("DELETE", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-items/${encodeURIComponent(id)}`).then(() => undefined);
}

/** Operator: hide a roll item for this cave without deleting it — a removable
 *  global or one of the cave's own (ADR 0254). */
export function suppressGlobalRollItem(caveId: string, rollItemId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("POST", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-suppress/${encodeURIComponent(rollItemId)}`).then(() => undefined);
}

/** Operator: re-show a hidden roll item; the server appends it to the end of the
 *  active sequence (ADR 0254). */
export function unsuppressGlobalRollItem(caveId: string, rollItemId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>("DELETE", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-suppress/${encodeURIComponent(rollItemId)}`).then(() => undefined);
}

/** Operator: save the playback order of one roll's VISIBLE items (globals + own
 *  mixed, slot A first — ADR 0254). 409 when stale (the visible set changed). */
export function setCaveRollOrder(caveId: string, roll: RollName, order: string[]): Promise<void> {
  return sendJSON<{ ok: boolean }>("PUT", `/api/v1/caves/${encodeURIComponent(caveId)}/roll-order`, { roll, order }).then(() => undefined);
}

/** Operator: upload an image/video + add it as the cave's own roll item. */
export function uploadCaveRollItem(
  caveId: string,
  field: "image" | "video",
  file: File,
  meta: { roll: RollName; title?: string; position?: number; durationMs?: number },
): Promise<RollItem> {
  return uploadRollAsset(`/api/v1/caves/${encodeURIComponent(caveId)}/roll-items/upload`, field, file, meta);
}

async function uploadRollAsset(
  path: string,
  field: "image" | "video",
  file: File,
  meta: { roll: RollName; title?: string; position?: number; durationMs?: number },
): Promise<RollItem> {
  const form = new FormData();
  form.append(field, file);
  form.append("roll", meta.roll);
  if (meta.title) form.append("title", meta.title);
  if (meta.position !== undefined) form.append("position", String(meta.position));
  if (meta.durationMs !== undefined) form.append("durationMs", String(meta.durationMs));
  const resp = await apiFetch(path, { method: "POST", body: form });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { item: RollItem }).item;
}

// -- public resolver read (the watch surface loops these) --

/** The cave's resolved pre/post/hold-roll playlists (public). */
export function fetchResolvedRoll(caveId: string): Promise<RollPlaylists> {
  return getJSON<RollPlaylists>(`/api/v1/public/cave/${encodeURIComponent(caveId)}/roll`);
}

// ── Recordings + on-demand VOD (ADR 0032) ───────────────────────────────────

export interface Recording {
  id: string;
  broadcastId: string;
  eventId: string;
  caveId: string;
  title: string;
  // The server also emits the intermediate ISO-capture states (ADR 0034).
  status: "pending" | "recording" | "captured" | "rendering" | "ready" | "failed";
  storageBackend?: string;
  maxViewsPerUser: number;
  availabilityHours: number;
  availableUntil?: string;
  createdAt: string;
  readyAt?: string;
  /** Why a `failed` recording failed — render/capture error (ADR 0099). */
  failureReason?: string;
  /** The recording's event start, so a row/card can disambiguate a recurring
   *  CaveCast that repeats the same title — `{title} · {date-time}` (ADR 0099). */
  eventStartsAt?: string;
  /** Replay sale (LSC-551 / ADR 0237): the admin replay-sale config. When
   *  `replaySaleEnabled`, a fan who doesn't already hold the originating event's
   *  entitlement can buy `replayTicketTypeId` via the normal checkout to watch. */
  replaySaleEnabled?: boolean;
  replayPriceCents?: number;
  replayTicketTypeId?: string;
  /** A recording captured during a private rehearsal (ADR 0244, LSC-507 #6): for crew
   *  review only — absent from the public On-Demand list, crew-only to watch. Flagged
   *  on operator surfaces so it can't be mistaken for the real show. */
  rehearsal?: boolean;
}

export interface RecordingSettings {
  title?: string;
  maxViewsPerUser: number;
  availabilityHours: number;
}

/** Operator: configure record + on-demand for an event (pre-live only). */
export function configureRecording(caveId: string, eventId: string, s: RecordingSettings): Promise<Recording> {
  return sendJSON<{ recording: Recording }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/recording`,
    s,
  ).then((r) => r.recording);
}

/** Operator: list a cave's recordings (any status). */
export async function listCaveRecordings(caveId: string): Promise<Recording[]> {
  const resp = await apiFetch(`/api/v1/caves/${encodeURIComponent(caveId)}/recordings`);
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return ((await resp.json()) as { recordings: Recording[] }).recordings;
}

/** Operator: edit the per-user view cap + availability window (enforced). */
export function updateRecording(id: string, s: RecordingSettings): Promise<Recording> {
  return sendJSON<{ recording: Recording }>("PUT", `/api/v1/recordings/${encodeURIComponent(id)}`, {
    maxViewsPerUser: s.maxViewsPerUser,
    availabilityHours: s.availabilityHours,
  }).then((r) => r.recording);
}

/**
 * Operator: re-arm a used recording (ADR 0041) — reset its ended broadcast to
 * idle + the recording to pending so the event can go live and record fresh.
 * The server refuses if the broadcast is still live/stopped or a published
 * recording would be overwritten.
 */
export function rearmRecording(id: string): Promise<Recording> {
  return sendJSON<{ recording: Recording }>("POST", `/api/v1/recordings/${encodeURIComponent(id)}/rearm`).then(
    (r) => r.recording,
  );
}

/**
 * Reclaim a FALSE-STARTED CaveCast (LSC-641/ADR 0248): reset a broadcast stuck `live`
 * but never aired (an accidental Director-console-open / Legend Go-Live→Exit before the
 * first TAKE) back to not-started — resets the broadcast to idle, re-arms the recording,
 * un-archives the event, stops the orphaned egress. cave_config. The immediate human
 * counterpart to the never-aired reaper (ADR 0239). The server refuses (409) if the show
 * actually aired, or a published/rendering VOD exists.
 */
export function reclaimCaveCast(caveId: string, eventId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/events/${encodeURIComponent(eventId)}/reclaim`,
  ).then(() => undefined);
}

/** One live-but-never-aired "false-start" (LSC-641) for the admin attention queue. */
export interface NeverAiredBroadcast {
  broadcastId: string;
  caveId: string;
  caveName?: string;
  eventId: string;
  eventTitle?: string;
  startedAt?: string | null;
}

/**
 * Platform overview (super-admin): the live-but-never-aired CaveCasts — a fan sees them
 * as "Live Now" but they never went on air (LSC-641/ADR 0248). Feeds the admin attention
 * queue so a stuck-live CaveCast is seen + reclaimed immediately.
 */
export function fetchNeverAiredBroadcasts(): Promise<NeverAiredBroadcast[]> {
  return getJSON<{ broadcasts: NeverAiredBroadcast[] }>("/api/v1/broadcasts/never-aired").then(
    (b) => b.broadcasts,
  );
}

/** Operator: finalize captured media (dev capture seam). */
export async function uploadRecordingMedia(id: string, file: File): Promise<Recording> {
  const form = new FormData();
  form.append("media", file);
  const resp = await apiFetch(`/api/v1/recordings/${encodeURIComponent(id)}/media`, { method: "POST", body: form });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { recording: Recording }).recording;
}

/** Admin/operator: the URL to download a recording's current media asset (LSC-488). */
export function recordingMediaUrl(id: string): string {
  // Admin dates are browser-local (sections.formatDateTime). Send the same zone
  // so an evening event does not download with the following day's UTC date.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  return `/api/v1/recordings/${encodeURIComponent(id)}/media?tz=${encodeURIComponent(zone)}`;
}

/** Admin/operator: atomically REPLACE a recording's media asset with a corrected /
 *  re-rendered file (LSC-488) — identity, entitlement, view cap, and window are
 *  preserved; a failed/invalid upload leaves the prior good asset serving. */
export async function replaceRecordingMedia(id: string, file: File): Promise<Recording> {
  const form = new FormData();
  form.append("media", file);
  const resp = await apiFetch(`/api/v1/recordings/${encodeURIComponent(id)}/media`, { method: "PUT", body: form });
  if (!resp.ok) throw new ApiError(resp.status, `replace failed (${resp.status})`);
  return ((await resp.json()) as { recording: Recording }).recording;
}

export interface PublicRecording {
  id: string;
  eventId: string;
  title: string;
  availableUntil?: string;
  maxViewsPerUser: number;
  /** The recording's event start (ADR 0099) — lets a card disambiguate repeated
   *  CaveCasts of the same title (LSC-420). Absent if the event is gone. */
  eventStartsAt?: string;
  /** Replay sale (LSC-551 / ADR 0237): present only when replay access is for sale.
   *  A fan who isn't entitled to the originating event buys `replayTicketTypeId`
   *  through the normal checkout to watch — the price is flat (cents). */
  replaySaleEnabled?: boolean;
  replayPriceCents?: number;
  replayTicketTypeId?: string;
  /** Whether the SIGNED-IN fan already has entitlement to watch this replay (via an
   *  event/pass holding, VIP, or operator access). Server-computed because the client
   *  can't derive past-event coverage (ADR 0163 eventIds is forward-looking). Absent
   *  when unauthenticated. Drives Watch (entitled) vs Buy Ticket (for sale + not). */
  entitled?: boolean;
}

/** Fan: the cave's currently-available on-demand recordings (public). The server
 *  returns only READY, in-window recordings. Throws an ApiError on a genuine
 *  failure so the caller can tell "service is down" apart from "no recordings"
 *  (ADR 0049/0109) — a 404 (unknown cave) is treated as no recordings. */
export async function fetchCaveRecordings(caveId: string): Promise<PublicRecording[]> {
  const resp = await apiFetch(`/api/v1/public/cave/${encodeURIComponent(caveId)}/recordings`);
  if (resp.status === 404) return [];
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return ((await resp.json()) as { recordings: PublicRecording[] }).recordings;
}

export interface RecordingWatch {
  /** "hls" = segmented VOD (instant long-form startup, ADR 0229) — play `url` via
   *  useHlsPlayback and keep `mp4Url` as the progressive fallback. "url" = a
   *  presigned direct MP4 (S3). "stream" = the authed disk MP4 stream route. */
  mode: "stream" | "url" | "hls";
  url: string;
  /** mode:"hls" only — the progressive MP4 (presigned or /stream) to fall back to
   *  when the client can't play HLS (ADR 0229). */
  mp4Url?: string;
  viewsUsed: number;
  viewsRemaining: number; // -1 = unlimited
  availableUntil?: string;
}

/** Fan: request an on-demand watch — the server enforces the cap + window. */
export function watchRecording(id: string): Promise<RecordingWatch> {
  return sendJSON<RecordingWatch>("POST", `/api/v1/recordings/${encodeURIComponent(id)}/watch`);
}

/** Admin: enable/disable paid replay access on a recording + set the price (LSC-551 /
 *  ADR 0237). Gated by cave_config AND catalog:write. Enabling provisions the hidden
 *  replay ticket type that backs the Fan's "Buy Ticket" checkout. */
export function setRecordingReplaySale(id: string, enabled: boolean, priceCents: number): Promise<Recording> {
  return sendJSON<{ recording: Recording }>("PUT", `/api/v1/recordings/${encodeURIComponent(id)}/replay-sale`, {
    enabled,
    priceCents,
  }).then((r) => r.recording);
}

// ── Manual entitlement revocation (ADR 0047) ────────────────────────────────

/** One holding joined with its buyer + ticket for the support surface. */
export interface Entitlement {
  holdingId: string;
  userId: number;
  username: string;
  displayName?: string;
  email?: string;
  ticketTypeId: string;
  ticketName: string;
  ticketKind: string;
  caveId: string;
  status: "active" | "revoked";
  purchasedAt: string;
  revokedAt?: string;
  revokeReason?: string;
}

/** Support (admin/super-admin): a cave's holdings, active + revoked. */
export async function listCaveEntitlements(caveId: string): Promise<Entitlement[]> {
  const resp = await apiFetch(`/api/v1/caves/${encodeURIComponent(caveId)}/entitlements`);
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return ((await resp.json()) as { entitlements: Entitlement[] }).entitlements;
}

/** The raw holding returned by a revoke. */
export interface RevokedHolding {
  id: string;
  status: "active" | "revoked";
  revokedAt?: string;
  revokeReason?: string;
}

/**
 * Support (admin/super-admin): revoke a Fan's watch entitlement with a reason
 * (ADR 0047). Records the reason only — automated Stripe refunds are deferred,
 * so any actual refund is handled by hand in Stripe.
 */
export function revokeEntitlement(holdingId: string, reason: string): Promise<RevokedHolding> {
  return sendJSON<{ holding: RevokedHolding }>(
    "POST",
    `/api/v1/holdings/${encodeURIComponent(holdingId)}/revoke`,
    { reason },
  ).then((r) => r.holding);
}

/** The root-admin comp-grant result (ADR 0223): mode "granted" (new holding) or "owned". */
export interface GrantResult {
  mode: "granted" | "owned";
  ticketTypeId: string;
  username: string;
}

/**
 * Comp-grant a ticket type to a user, bypassing payment (ADR 0223). Root account
 * ONLY (the API enforces it with 403 otherwise); idempotent — a user who already
 * holds the type comes back as { mode: "owned" }.
 */
export function grantCompTicket(ticketTypeId: string, username: string): Promise<GrantResult> {
  return sendJSON<GrantResult>(
    "POST",
    `/api/v1/ticket-types/${encodeURIComponent(ticketTypeId)}/grant`,
    { username },
  );
}

/** The viewer delivery seam: WebRTC in dev, HLS (CDN) in prod. */
export interface WatchSource {
  mode: "webrtc" | "hls";
  url?: string; // ws SFU URL (webrtc) or HLS playlist URL (hls)
  token?: string; // viewer join token (webrtc)
  room?: string;
  /** The cave the broadcast belongs to — the watch surface resolves its
   *  pre/post-roll looping playlists from it (ADR 0052). */
  caveId?: string;
  program: ProgramState;
}

/** A ticket the caller holds. Only ACTIVE (non-revoked) holdings appear — a revoked
 *  entitlement drops out (ADR 0047, LSC-345). */
export interface HeldTicket {
  ticketTypeId: string;
  name: string;
  kind: string;
  caveId: string;
  caveName?: string;
  /** Ids of the upcoming covered events (LSC-356); empty when none upcoming. */
  eventIds?: string[];
}

/** Director: read the current program state. */
export function fetchBroadcastProgram(broadcastId: string): Promise<ProgramState> {
  return getJSON<{ program: ProgramState }>(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/program`,
  ).then((r) => r.program);
}

/** Viewer: poll the current program state (ticket-gated). A backstop for a missed
 *  LiveKit-data program update so a fan still converges — e.g. a lower-third taken
 *  live that some viewers never saw (ADR 0091). */
export function fetchWatchProgram(broadcastId: string): Promise<ProgramState> {
  return getJSON<{ program: ProgramState }>(
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/watch/program`,
  ).then((r) => r.program);
}

/** Director: set the program state (also publish it as a LiveKit data message
 *  for instant fan-out; this call persists it for late-joiners). */
export function setBroadcastProgram(broadcastId: string, program: ProgramState): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/program`,
    program,
  ).then(() => undefined);
}

/** Viewer: mint a ticket-gated watch source (403 without a covering ticket). */
export function watchBroadcast(broadcastId: string): Promise<WatchSource> {
  return sendJSON<WatchSource>(
    "POST",
    `/api/v1/broadcasts/${encodeURIComponent(broadcastId)}/watch`,
  );
}

/** Join a program-render session by its code (LSC-429) — PUBLIC, code-authed. Returns
 *  a subscribe-only WatchSource + the current program so the headless renderer composes
 *  the PRODUCED program for the LiveKit composite egress (HLS/VOD). No session. */
export function renderJoin(code: string): Promise<WatchSource> {
  return sendJSON<WatchSource>(
    "POST",
    `/api/v1/broadcasts/render/${encodeURIComponent(code)}/join`,
  );
}

/** Acquire a ticket type (dev-free; the direct grant used by the CLI/tests). */
export function acquireTicket(ticketTypeId: string): Promise<void> {
  return sendJSON<{ ok: boolean }>(
    "POST",
    `/api/v1/ticket-types/${encodeURIComponent(ticketTypeId)}/acquire`,
  ).then(() => undefined);
}

/**
 * How a checkout resolved: a Stripe redirect, an immediate grant, or a no-op
 * because the fan already has access — `owned` (holds this exact type) or
 * `covered` (a broader pass already covers every upcoming CaveCast, LSC-343).
 */
export type CheckoutMode = "stripe" | "dev" | "free" | "owned" | "covered";

/** The result of starting a ticket purchase (ADR 0014). */
export interface CheckoutResult {
  mode: CheckoutMode;
  /** Present when mode === "stripe": the hosted Checkout URL to redirect to. */
  url?: string;
  sessionId?: string;
  ticketTypeId?: string;
}

/**
 * Start a ticket purchase. With Stripe configured and a priced ticket the
 * result is {mode:"stripe", url} — send the browser there. Otherwise the ticket
 * is granted immediately (mode "free" for $0, "dev" when Stripe is unconfigured).
 *
 * `returnPath` is the storefront page the buy was initiated from; the server
 * sanitizes it and uses it to build the Stripe success/cancel return URLs, so
 * the buyer lands back on the exact cave/legend page — not the generic root
 * (ADR 0048).
 */
export function startTicketCheckout(ticketTypeId: string, returnPath?: string): Promise<CheckoutResult> {
  // GA4 funnel (ADR 0224): the fan initiated checkout. Recommended ecommerce
  // event name; ids only (no PII). No-op unless a GA4 id is configured. The
  // first-party commerce events stay SERVER-emitted (ADR 0017) — this is additive.
  gaEvent("begin_checkout", { product_id: ticketTypeId });
  return sendJSON<CheckoutResult>(
    "POST",
    `/api/v1/ticket-types/${encodeURIComponent(ticketTypeId)}/checkout`,
    returnPath ? { returnPath } : undefined,
  );
}

/** Whether real Stripe checkout is live (drives the buy-button label). */
export interface PaymentsConfig {
  stripeEnabled: boolean;
  currency: string;
}

/** Public: is Stripe checkout configured? */
export function fetchPaymentsConfig(): Promise<PaymentsConfig> {
  return getJSON<PaymentsConfig>("/api/v1/payments/config");
}

/** One purchase in the caller's history. */
export interface PaymentRecord {
  ticketTypeId: string;
  name?: string;
  caveId: string;
  amountCents: number;
  currency: string;
  status: string;
  createdAt: string;
}

/** List the caller's purchases, newest first. */
export function fetchMyPayments(): Promise<PaymentRecord[]> {
  return getJSON<{ payments: PaymentRecord[] }>("/api/v1/me/payments").then((r) => r.payments ?? []);
}

/** Per-cave sales rollup (ADR 0016). */
export interface CaveMetric {
  caveId: string;
  caveName: string;
  revenueCents: number;
  paidTickets: number;
  ticketsHeld: number;
  // Comped (free-grant) tickets + their foregone list-price value (ADR 0230).
  compedTickets: number;
  compedValueCents: number;
}

/** Platform sales overview — aggregate + per-cave (super-admin only). */
export interface MetricsOverview {
  currency: string;
  aggregate: {
    revenueCents: number;
    paidTickets: number;
    pendingCheckouts: number;
    ticketsHeld: number;
    buyers: number;
    // Comped tickets + foregone value, platform-wide (ADR 0230).
    compedTickets: number;
    compedValueCents: number;
  };
  perCave: CaveMetric[];
}

/** Super-admin: tickets sold + revenue, aggregate and per-cave. */
export function fetchMetrics(): Promise<MetricsOverview> {
  return getJSON<MetricsOverview>("/api/v1/metrics/overview");
}

// ---- Marketing / CRM / Recommendation admin (ADR 0198) -----------------------------------

export interface IntegrationHealth {
  enabled: boolean;
  ok: boolean;
  detail?: string;
}
export interface MarketingHealth {
  integrations: Record<"twenty" | "gorse" | "mautic", IntegrationHealth>;
  counts: { customers?: number; legends?: number; caves?: number };
}
export interface MarketingCustomerRow {
  userId: number;
  name: string;
  email: string;
  accountStatus: string;
  ownedCaves: number;
  ownedSports: string[];
  lifetimeValueCents: number;
}
export interface MarketingRecommendation {
  caveId: string;
  name?: string;
  legendId?: string;
  legendName?: string;
  sport?: string;
  score: number;
}
export interface MarketingCustomerDetail {
  userId: number;
  name: string;
  email: string;
  accountStatus: string;
  marketingConsent: boolean;
  lifetimeValueCents: number;
  ownedSports: string[];
  ownedLegendIds: string[];
  caves: {
    caveId: string;
    name: string;
    status: string;
    ticketKind: string;
    legendName?: string;
    sport?: string;
  }[];
  recommendations: MarketingRecommendation[];
  segmentTags: string[];
  crm: { twentyUrl: string };
  integrations: Record<string, boolean>;
}
export interface BackfillResult {
  domain: string;
  processed: number;
  succeeded: number;
  failed: number;
  dryRun: boolean;
  byEntity?: Record<string, number>;
  note?: string;
}

/** Super-admin: integration connectivity + record counts. */
export function fetchMarketingHealth(): Promise<MarketingHealth> {
  return getJSON<MarketingHealth>("/api/v1/marketing/health");
}

/** Super-admin: paginated customers with their CRM/marketing summary. */
export function fetchMarketingCustomers(opts?: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<{
  customers: MarketingCustomerRow[];
  total: number;
  limit: number;
  offset: number;
}> {
  const p = new URLSearchParams();
  if (opts?.limit) p.set("limit", String(opts.limit));
  if (opts?.offset) p.set("offset", String(opts.offset));
  if (opts?.q) p.set("q", opts.q);
  const qs = p.toString();
  return getJSON(`/api/v1/marketing/customers${qs ? "?" + qs : ""}`);
}

/** Super-admin: one customer's full marketing view. */
export function fetchMarketingCustomer(
  userId: number,
): Promise<MarketingCustomerDetail> {
  return getJSON<{ customer: MarketingCustomerDetail }>(
    `/api/v1/marketing/customers/${userId}`,
  ).then((b) => b.customer);
}

/** Super-admin: recommendations for a user (admin debug view). */
export function fetchMarketingRecommendations(
  userId: number,
  limit = 10,
): Promise<{
  userId: number;
  recommendations: MarketingRecommendation[];
  gorseEnabled: boolean;
  error?: string;
}> {
  return getJSON(`/api/v1/marketing/recommendations/${userId}?limit=${limit}`);
}

/** Super-admin: run a backfill (crm|recommendations|marketing). */
export function runMarketingBackfill(
  domain: "crm" | "recommendations" | "marketing",
  opts?: { dryRun?: boolean; batchSize?: number },
): Promise<BackfillResult> {
  return sendJSON<BackfillResult>("POST", `/api/v1/marketing/backfill/${domain}`, {
    dryRun: opts?.dryRun ?? false,
    batchSize: opts?.batchSize ?? 0,
  });
}

/** Fan self-serve: recommended caves for the logged-in user. */
export function fetchMyRecommendations(
  limit = 10,
): Promise<MarketingRecommendation[]> {
  return getJSON<{ recommendations: MarketingRecommendation[] }>(
    `/api/v1/me/recommendations?limit=${limit}`,
  ).then((b) => b.recommendations ?? []);
}

/** Super-admin: recording count per status (admin attention queue, ADR 0160). */
export function fetchRecordingStats(): Promise<Record<string, number>> {
  return getJSON<{ byStatus: Record<string, number> }>(
    "/api/v1/recordings/stats",
  ).then((b) => b.byStatus);
}

// ── legends & discovery (ADR 0020) ───────────────────────────────────────────

/** A legend in the selector taxonomy (no profile body). */
export interface LegendLite {
  id: string;
  name: string;
  position: string;
  years: string;
  initials: string;
  photo: string;
  /** "" when the legend has no live cave to enter yet. */
  caveId: string;
  /** Super-admin-curated: surfaced on the selector before the funnel. */
  featured: boolean;
}

export interface LegendStat {
  val: string;
  label: string;
}
export interface LegendCavecast {
  date: string;
  title: string;
  badge: "live" | "vod";
}
export interface LegendCharity {
  name: string;
  desc: string;
}
export interface LegendBiz {
  initials: string;
  name: string;
  type: string;
  link: string;
}
export interface SponsorSlot {
  tier: string;
  brand: string;
  offer: string;
}
export interface LegendSponsors {
  presentedBy: SponsorSlot[];
  pills: string[];
}
export interface LegendProfile {
  sub: string;
  tags: string[];
  stats: LegendStat[];
  cavecasts: LegendCavecast[];
  aboutTitle: string;
  bio: string;
  roles: string;
  charities: LegendCharity[];
  biz: LegendBiz[];
  sponsors?: LegendSponsors | null;
}
export interface Legend extends LegendLite {
  sport: string;
  level: string;
  team: string;
  profile: LegendProfile | null;
}

/** The selector taxonomy, grouped sport → level → team → legends. */
export interface TeamGroup {
  team: string;
  legends: LegendLite[];
}
export interface LevelGroup {
  level: string;
  teams: TeamGroup[];
}
export interface SportGroup {
  sport: string;
  levels: LevelGroup[];
}

/** The full discovery roster grouped for the selector funnel. */
export function fetchLegendTaxonomy(): Promise<SportGroup[]> {
  return getJSON<{ sports: SportGroup[] }>("/api/v1/public/legends").then((b) => b.sports ?? []);
}

/** The super-admin-curated featured legends (selector top row). */
export function fetchFeaturedLegends(): Promise<LegendLite[]> {
  return getJSON<{ legends: LegendLite[] }>("/api/v1/public/legends/featured").then((b) => b.legends ?? []);
}

/** A roster row in the admin legend curator. */
export interface AdminLegend extends LegendLite {
  sport: string;
  level: string;
  team: string;
  archived?: boolean;
}

/** Super-admin: the roster (optionally filtered by name/team) for curation. Pass
 *  includeArchived to include retired legends (ADR 0162) — each row's `archived`
 *  flag drives the Show-archived view. */
export function fetchAdminLegends(q = "", includeArchived = false): Promise<AdminLegend[]> {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (includeArchived) params.set("archived", "1");
  const qs = params.toString() ? `?${params}` : "";
  return getJSON<{ legends: AdminLegend[] }>(`/api/v1/legends${qs}`).then((b) => b.legends ?? []);
}

/** Super-admin: retire a legend (ADR 0162) — drops it from discovery + the active
 *  roster, retaining its row + links. Idempotent. */
export async function archiveLegend(id: string): Promise<void> {
  await sendJSON("POST", `/api/v1/legends/${encodeURIComponent(id)}/archive`, {});
}

/** Super-admin: restore a retired legend to discovery + the active roster (ADR 0162). */
export async function unarchiveLegend(id: string): Promise<void> {
  await sendJSON("POST", `/api/v1/legends/${encodeURIComponent(id)}/unarchive`, {});
}

/** Super-admin: hard-delete a PRISTINE legend (ADR 0162). Rejects (409) when the
 *  legend headlines a cave or maps to an account — unlink or archive instead. */
export async function deleteLegend(id: string): Promise<void> {
  await sendJSON("DELETE", `/api/v1/legends/${encodeURIComponent(id)}`);
}

/** Super-admin: feature or un-feature a legend on the selector. */
export async function setLegendFeatured(id: string, featured: boolean): Promise<void> {
  await sendJSON<{ id: string; featured: boolean }>(
    "PUT",
    `/api/v1/legends/${encodeURIComponent(id)}/featured`,
    { featured },
  );
}

/** Super-admin: upload a legend headshot; returns the rooted /uploads/… path (LSC-407). */
export async function uploadLegendPhoto(id: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("image", file);
  const resp = await apiFetch(`/api/v1/legends/${encodeURIComponent(id)}/photo`, { method: "POST", body: form });
  if (!resp.ok) throw new ApiError(resp.status, `upload failed (${resp.status})`);
  return ((await resp.json()) as { photo: string }).photo;
}

/** Super-admin: clear a legend's headshot back to the initials fallback (LSC-407). */
export async function removeLegendPhoto(id: string): Promise<void> {
  await apiFetch(`/api/v1/legends/${encodeURIComponent(id)}/photo`, { method: "DELETE" }).then((r) => {
    if (!r.ok) throw new ApiError(r.status, `remove failed (${r.status})`);
  });
}

/** Super-admin: create a platform legend (ADR 0025). */
export interface NewLegend {
  name: string;
  sport: string;
  level: string;
  team: string;
  position?: string;
  caveId?: string;
  featured?: boolean;
}
export function createLegend(input: NewLegend): Promise<AdminLegend> {
  return sendJSON<{ legend: AdminLegend }>("POST", "/api/v1/legends", input).then((b) => b.legend);
}

/** Super-admin: link (or unlink with "") a legend to a cave headline + grant the
 *  legend's operators cave-scoped access (ADR 0025). Returns the id of any legend
 *  bumped off that cave (LSC-296) so the caller can surface the reassignment. */
export async function linkLegendCave(id: string, caveId: string): Promise<string | undefined> {
  const r = await sendJSON<{ ok: boolean; bumpedLegendId?: string }>(
    "PUT",
    `/api/v1/legends/${encodeURIComponent(id)}/cave`,
    { caveId },
  );
  return r.bumpedLegendId || undefined;
}

/** The admin Mapping screen data: every cave↔legend↔user link (LSC-296). */
export interface CaveMapping {
  id: string;
  name: string;
  host: string;
  archived: boolean;
  legendId?: string;
  legendName?: string;
  legendUserId?: number;
  legendUsername?: string;
}
export interface MappingLegend {
  id: string;
  name: string;
  sport: string;
  caveId: string;
  userId?: number;
  username?: string;
}
export interface MappingUser {
  id: number;
  username: string;
}
export interface CaveLegendMappings {
  caves: CaveMapping[];
  legends: MappingLegend[];
  users: MappingUser[];
}

/** Super-admin: every cave↔legend↔user mapping for the admin Mapping screen. */
export function fetchCaveLegendMappings(): Promise<CaveLegendMappings> {
  return getJSON<CaveLegendMappings>("/api/v1/legends/cave-mappings");
}

// ── audit log (ADR 0160) ─────────────────────────────────────────────────────

/** One back-office action recorded in the append-only audit log. */
export interface AuditEntry {
  id: string;
  at: string;
  actor?: string;
  action: string;
  objectType?: string;
  objectId?: string;
  objectDisplay?: string;
  caveId?: string;
  reason?: string;
  metadata?: string;
  ip?: string;
  impersonating?: boolean;
}

export interface AuditFilter {
  action?: string;
  cave?: string;
  actor?: string;
  limit?: number;
}

/** Super-admin: the audit log, newest-first, optionally filtered. */
export function fetchAudit(f: AuditFilter = {}): Promise<AuditEntry[]> {
  const q = new URLSearchParams();
  if (f.action) q.set("action", f.action);
  if (f.cave) q.set("cave", f.cave);
  if (f.actor) q.set("actor", f.actor);
  if (f.limit) q.set("limit", String(f.limit));
  const qs = q.toString();
  return getJSON<{ entries: AuditEntry[] }>(
    "/api/v1/audit" + (qs ? `?${qs}` : ""),
  ).then((b) => b.entries);
}

/** Super-admin: bind a user account to a legend as its durable identity (ADR 0038). */
export async function linkLegendUser(id: string, username: string): Promise<void> {
  await sendJSON<{ ok: boolean }>(
    "PUT",
    `/api/v1/legends/${encodeURIComponent(id)}/user`,
    { username },
  );
}

/** One legend with its full profile (or throws ApiError 404). */
export function fetchLegend(id: string): Promise<Legend> {
  return getJSON<{ legend: Legend }>(`/api/v1/public/legends/${encodeURIComponent(id)}`).then((b) => b.legend);
}

/** The legend headlining a cave, or null when none is linked. */
export async function fetchCaveLegend(caveId: string): Promise<Legend | null> {
  try {
    const b = await getJSON<{ legend: Legend }>(`/api/v1/public/cave/${encodeURIComponent(caveId)}/legend`);
    return b.legend;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ── Cave Lobby (ADR 0129/0130) ───────────────────────────────────────────────

/** A lobby module kind — the approved vocabulary (spec §4). No arbitrary HTML. */
export type LobbyModuleKind =
  | "story"
  | "linkedCards"
  | "stats"
  | "programming"
  | "partners"
  | "social";

/** A module's desktop width on the 6-column grid: full=6 / twoThirds=4 / half=3 / third=2. */
export type LobbySpan = 6 | 4 | 3 | 2;

export interface LobbyStoryContent {
  body: string;
  imageAssetId?: string;
  imageAlt?: string;
  caption?: string;
  linkUrl?: string;
  linkLabel?: string;
}
export interface LobbyLinkedCard {
  title: string;
  desc?: string;
  imageAssetId?: string;
  imageAlt?: string;
  actionLabel?: string;
  actionUrl?: string;
}
export interface LobbyLinkedCardsContent {
  cards: LobbyLinkedCard[];
}
/** An editorial career fact (label/value) — NOT a live sports stat. */
export interface LobbyStatItem {
  val: string;
  label: string;
}
export interface LobbyStatsContent {
  items: LobbyStatItem[];
}
/** Programming renders real upcoming/live/replays from the Cave services (spec §4). */
export interface LobbyProgrammingContent {
  mode: "upcoming" | "replays" | "both";
  previewCount: number;
  /** Optional pinned event ids (else the automatic upcoming feed). */
  eventIds?: string[];
  /** Optional pinned recording ids (else the automatic replay feed). */
  recordingIds?: string[];
}
export interface LobbyPartner {
  name: string;
  tier?: string;
  logoAssetId?: string;
  logoAlt?: string;
  url?: string;
}
export interface LobbyPartnersContent {
  entries: LobbyPartner[];
}
export interface LobbySocialLink {
  platform: string;
  url: string;
}
export interface LobbySocialContent {
  links: LobbySocialLink[];
}

/** One editorial block. `content` is dispatched to a typed struct by `kind`. */
export interface LobbyModule {
  id: string;
  kind: LobbyModuleKind;
  title: string;
  /** Overrides the viewer-facing section eyebrow (LSC-258); empty = the LSC default. */
  label?: string;
  span: LobbySpan;
  hidden?: boolean;
  /** Kept opaque here (the renderer narrows by kind) — mirrors the server's RawMessage. */
  content: unknown;
}

/** The hero identity band: a portrait asset (empty = initials fallback) and an
 *  explicit featured offer that references a real ticket type (never derived from
 *  placement — spec §3). Empty featuredTicketTypeId = no promoted offer. */
export interface LobbyHero {
  portraitAssetId?: string;
  featuredTicketTypeId?: string;
  /** Achievement "pills" under the legend's name (e.g. "6× Super Bowl"), editable
   *  in the Designer (LSC-294). Seeded from the legend's profile.tags. */
  achievements?: string[];
}

/** The whole lobby: a hero + an ordered list of modules. */
export interface LobbyDocument {
  schemaVersion: number;
  hero: LobbyHero;
  /** Optional cave-level backdrop image (asset id) for the public lobby (LSC-260);
   *  empty = the default LSC treatment. */
  background?: string;
  /** Optional per-Cave accent colour (#rrggbb; empty = the default LSC gold) — the
   *  branding primitive, rendered where the gold accent is (CTAs, offer, hairlines),
   *  not a background or text colour (LSC-413). A single controlled value. */
  accent?: string;
  modules: LobbyModule[];
}

/** The compact per-Cave branding projection (LSC-413) — the subset the native
 *  clients render without parsing the whole lobby document. `accent` is #rrggbb (or
 *  absent = default gold; fall back to your default if it fails your contrast bar).
 *  The URLs are rooted /uploads/… served paths (absent when unset). */
export interface LobbyBranding {
  name: string;
  accent?: string;
  backgroundUrl?: string;
  portraitUrl?: string;
}

/** A resolved lobby asset (id → served URL + metadata), so the renderer never
 *  handles a raw asset id or a client-supplied image URL (ADR 0131 §7). */
export interface LobbyAsset {
  url: string;
  alt: string;
  width?: number;
  height?: number;
}

/** The public lobby read: the published document, or a default-from-profile
 *  fallback when nothing is published (`source` says which — ADR 0129/0130), plus
 *  a resolved id→asset map for every image the document references (ADR 0131). */
export interface PublicLobby {
  lobby: LobbyDocument;
  source: "published" | "default";
  /** Referenced asset ids resolved to their served URL + alt; absent/empty when
   *  the document references no images (or they were since deleted). */
  assets?: Record<string, LobbyAsset>;
  /** Compact per-Cave branding projection (LSC-413) — accent + rooted asset URLs +
   *  the Cave name, so a client renders branding without parsing the whole document. */
  branding?: LobbyBranding;
}

/** Public: a cave's lobby presentation (unauthenticated). Falls back to a
 *  default-from-profile document when nothing is published, so every cave has a
 *  usable entrance; the renderer honors `source` to decide when to take over from
 *  the legacy LegendCave storefront (Phase B rollout). */
export function fetchPublicLobby(caveId: string): Promise<PublicLobby> {
  return getJSON<PublicLobby>(`/api/v1/public/cave/${encodeURIComponent(caveId)}/lobby`);
}

// ── Cave Lobby editor (ADR 0131, cave_config) ────────────────────────────────

/** The editor view of a cave's lobby: the working draft + publication metadata. */
export interface LobbyEditorView {
  draft: LobbyDocument;
  draftRev: number;
  publishedRev: number;
  publishedAt?: string;
  publishedBy?: string;
  hasCustom: boolean;
  /** Whether the CALLER may publish this cave's lobby — admin OR a per-cave grant
   *  (LSC-262). The editor's Publish control reflects this, not just tenant admin. */
  canPublish: boolean;
}

/** One uploaded lobby asset row in the editor's asset library. */
export interface LobbyAssetRow {
  id: string;
  url: string;
  alt: string;
  width: number;
  height: number;
}

/** One published revision (restore source + history). */
export interface LobbyRevisionRow {
  rev: number;
  publishedBy: string;
  publishedAt?: string;
  doc: LobbyDocument;
}

/** `?front=<id>` selects which Cave (front) to author (ADR 0186 Phase 2); empty/omitted
 *  = the backend's default front. The document lobby calls carry it; asset/ticket/legend
 *  reads stay backend-scoped (assets are shared). */
function frontQ(frontId?: string): string {
  return frontId ? `?front=${encodeURIComponent(frontId)}` : "";
}

/** Editor: load the working draft (a default-from-profile seed when none exists). */
export function fetchLobbyEditor(caveId: string, frontId?: string): Promise<LobbyEditorView> {
  return getJSON<LobbyEditorView>(`/api/v1/caves/${encodeURIComponent(caveId)}/lobby${frontQ(frontId)}`);
}

/** Editor: validate + save the working draft at an expected revision (409 on stale). */
export function saveLobbyDraft(caveId: string, doc: LobbyDocument, expectedRev: number, frontId?: string): Promise<{ draftRev: number }> {
  return sendJSON<{ draftRev: number }>("PUT", `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/draft${frontQ(frontId)}`, {
    doc,
    expectedRev,
  });
}

/** Editor: publish the current draft as a new immutable revision (admin only). */
export function publishLobby(caveId: string, expectedRev: number, frontId?: string): Promise<{ publishedRev: number; publishedBy: string }> {
  return sendJSON<{ publishedRev: number; publishedBy: string }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/publish${frontQ(frontId)}`,
    { expectedRev },
  );
}

/** Editor: the published-revision history, newest first. */
export function fetchLobbyRevisions(caveId: string, frontId?: string): Promise<LobbyRevisionRow[]> {
  return getJSON<{ revisions: LobbyRevisionRow[] }>(
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/revisions${frontQ(frontId)}`,
  ).then((b) => b.revisions ?? []);
}

/** Editor: copy a past published revision into the draft (starts a new draft). */
export function restoreLobbyRevision(caveId: string, rev: number, expectedRev: number, frontId?: string): Promise<{ draftRev: number }> {
  return sendJSON<{ draftRev: number }>("POST", `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/restore${frontQ(frontId)}`, {
    rev,
    expectedRev,
  });
}

/** Editor: a cave's uploaded lobby images. */
export function fetchLobbyAssets(caveId: string): Promise<LobbyAssetRow[]> {
  return getJSON<{ assets: LobbyAssetRow[] }>(
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/assets`,
  ).then((b) => b.assets ?? []);
}

/** Editor: upload a lobby image (multipart) with required alt text. */
export async function uploadLobbyAsset(caveId: string, file: File, alt: string): Promise<LobbyAssetRow> {
  const form = new FormData();
  form.append("image", file);
  form.append("alt", alt);
  const resp = await apiFetch(`/api/v1/caves/${encodeURIComponent(caveId)}/lobby/assets`, {
    method: "POST",
    body: form,
  });
  if (!resp.ok) {
    let message = `upload failed (${resp.status})`;
    try {
      const data = (await resp.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, message);
  }
  return ((await resp.json()) as { asset: LobbyAssetRow }).asset;
}

/** Editor: delete a lobby asset (revision-safe on the server — 409 if a published
 *  revision still references it; the 204 success carries no body). */
export async function deleteLobbyAsset(caveId: string, assetId: string): Promise<void> {
  const resp = await apiFetch(
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/assets/${encodeURIComponent(assetId)}`,
    { method: "DELETE" },
  );
  if (!resp.ok) {
    let message = `delete failed (${resp.status})`;
    try {
      const data = (await resp.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, message);
  }
}

// ── Cave Lobby device preview (ADR 0134 part B) ──────────────────────────────

/** Editor: mint an ephemeral device-preview session (a code + a URL to QR-encode). */
export function createLobbyPreviewSession(caveId: string): Promise<{ code: string; url: string; expiresAt: string }> {
  return sendJSON<{ code: string; url: string; expiresAt: string }>(
    "POST",
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/preview-session`,
  );
}

/** Editor: push the current render bundle to a device-preview session (opaque body). */
export async function pushLobbyPreviewSnapshot(caveId: string, code: string, bundle: unknown): Promise<void> {
  const resp = await apiFetch(
    `/api/v1/caves/${encodeURIComponent(caveId)}/lobby/preview-session/${encodeURIComponent(code)}`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(bundle) },
  );
  if (!resp.ok) throw new ApiError(resp.status, `preview push failed (${resp.status})`);
}

/** Device (public, code-authed): poll the latest bundle. Returns null while the
 *  editor hasn't pushed yet; throws ApiError(404) when the code is invalid/expired. */
export async function fetchLobbyPreviewSnapshot<T>(code: string): Promise<T | null> {
  const resp = await apiFetch(`/api/v1/public/lobby-preview/${encodeURIComponent(code)}`);
  if (!resp.ok) throw new ApiError(resp.status, `preview read failed (${resp.status})`);
  const b = (await resp.json()) as { bundle: T | null };
  return b.bundle ?? null;
}

// ── analytics (ADR 0017) ─────────────────────────────────────────────────────

/** One row of the analytics event-count summary (super-admin). */
export interface AnalyticsEventCount {
  name: string;
  count: number;
  users: number;
}

export interface AnalyticsSummary {
  includeInternal: boolean;
  events: AnalyticsEventCount[];
}

/** Super-admin: behavioural event counts (staff traffic excluded by default). */
export function fetchAnalyticsSummary(includeInternal = false): Promise<AnalyticsSummary> {
  return getJSON<AnalyticsSummary>(
    `/api/v1/analytics/summary${includeInternal ? "?internal=1" : ""}`,
  );
}

// Client-emittable analytics events (identity/commerce are server-emitted).
export type ClientEventName =
  | "cave_viewed"
  | "cavecast_viewed"
  | "live_joined"
  | "live_left"
  | "playback_failed"
  // A cave shown to a fan (featured/discovery) but not opened — a disinterest read signal
  // for the recommender (ADR 0206).
  | "cave_impression"
  // The Legend's chroma compositor fell back to the raw camera (green screen on air)
  // because the background didn't load or the GPU couldn't upload it — a support-triage
  // signal carrying the reason + GPU renderer + texture ceiling (LSC-623). Operator-emitted.
  | "chroma_fallback"
  // The resilient background loader RESCUED a load — the crossOrigin image failed and the
  // same-origin blob fallback succeeded. The recovery counterpart to chroma_fallback:
  // confirms CORS/cache (not GPU) was the latent cause and that the /uploads ACAO header
  // isn't reaching this operator (LSC-623 follow-up). Operator-emitted.
  | "chroma_bg_recovered";

const ANON_KEY = "lsc_aid";
const FIRST_TOUCH_KEY = "lsc_first_touch";
const SESSION_KEY = "lsc_session";

// Storage in some environments (private mode, SSR shims, hardened browsers) can
// be absent or throw — every access is best-effort so analytics never breaks a
// screen. The anonymous id survives via the cookie even when Storage is dead.
function safeGet(store: "local" | "session", key: string): string | null {
  try {
    return (store === "local" ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}
function safeSet(store: "local" | "session", key: string, value: string): void {
  try {
    (store === "local" ? window.localStorage : window.sessionStorage).setItem(key, value);
  } catch {
    /* ignore */
  }
}
function readCookie(key: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** A short random id (device / session scope) without external deps. */
function randomId(): string {
  const c = window.crypto;
  if (c && "randomUUID" in c) return c.randomUUID();
  return Math.abs(Date.now() ^ (performance.now() * 1e6)).toString(36) + Math.random().toString(36).slice(2);
}

/**
 * Stable per-device anonymous id, mirrored into a cookie scoped to the parent
 * domain so it survives cave1/cave2 sub-domain hops and reaches the server on
 * the collector request (ADR 0017). The cookie is the source of truth so the id
 * is stable even when Storage is unavailable.
 */
function anonymousId(): string {
  let id = safeGet("local", ANON_KEY) ?? readCookie(ANON_KEY);
  if (!id) {
    id = randomId();
  }
  safeSet("local", ANON_KEY, id);
  // Widen the cookie to the registrable domain (…legendsportscave.com) so all
  // caves share one anonymous identity; falls back to host on localhost.
  const host = window.location.hostname;
  const parts = host.split(".");
  const domainAttr =
    parts.length >= 2 && host !== "localhost"
      ? `; domain=.${parts.slice(-2).join(".")}`
      : "";
  document.cookie = `${ANON_KEY}=${id}; path=/; max-age=31536000; SameSite=Lax${domainAttr}`;
  return id;
}

/** Per-tab session id (resets when the tab is closed). */
function sessionId(): string {
  let id = safeGet("session", SESSION_KEY);
  if (!id) {
    id = randomId();
    safeSet("session", SESSION_KEY, id);
  }
  return id;
}

/** UTM/referrer captured at first touch, preserved verbatim thereafter. */
function firstTouch(): { source: string; params: Record<string, string> } {
  const params = new URLSearchParams(window.location.search);
  const current = params.get("utm_source") ?? "";
  const stored = safeGet("local", FIRST_TOUCH_KEY);
  if (stored) return { source: stored, params: utmParams(params) };
  const source = current || (document.referrer ? "referral" : "direct");
  safeSet("local", FIRST_TOUCH_KEY, source);
  return { source, params: utmParams(params) };
}

function utmParams(params: URLSearchParams): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of ["source", "medium", "campaign", "content"]) {
    const v = params.get(`utm_${k}`);
    if (v) out[k] = v;
  }
  return out;
}

/**
 * Fire-and-forget analytics beacon. The server stamps identity + the internal
 * flag; the client only supplies context. Never throws — analytics must not
 * break a screen.
 */
export function track(
  name: ClientEventName,
  ctx: { caveId?: string; legendId?: string; cavecastId?: string; productId?: string; props?: unknown } = {},
): void {
  try {
    const ft = firstTouch();
    const body = {
      name,
      anonymousId: anonymousId(),
      sessionId: sessionId(),
      caveId: ctx.caveId ?? "",
      legendId: ctx.legendId ?? "",
      cavecastId: ctx.cavecastId ?? "",
      productId: ctx.productId ?? "",
      utmSource: ft.params.source ?? "",
      utmMedium: ft.params.medium ?? "",
      utmCampaign: ft.params.campaign ?? "",
      utmContent: ft.params.content ?? "",
      referrer: document.referrer ?? "",
      firstTouchSource: ft.source,
      props: ctx.props ? JSON.stringify(ctx.props) : "",
    };
    apiFetch("/api/v1/analytics/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
    // GA4 is ADDITIVE (ADR 0224): forward the same funnel event (ids only, no PII)
    // to Google Analytics. A no-op unless a GA4 id is configured; never throws.
    gaTrack(name, ctx);
  } catch {
    /* analytics is best-effort */
  }
}

/** List the tickets the caller holds. */
export function fetchMyTickets(): Promise<HeldTicket[]> {
  return getJSON<{ tickets: HeldTicket[] }>("/api/v1/me/tickets").then((r) => r.tickets ?? []);
}

/** A currently-live broadcast surfaced on a cave's public page. */
export interface LiveBroadcast {
  broadcastId: string;
  eventId: string;
  title: string;
}

/** Public: what's live at a cave right now (unauthenticated). */
export function fetchCaveLive(caveId: string): Promise<LiveBroadcast[]> {
  return getJSON<{ live: LiveBroadcast[] }>(
    `/api/v1/public/cave/${encodeURIComponent(caveId)}/live`,
  ).then((r) => r.live ?? []);
}

// ── impersonation (parallel-tab: token in the body, cookie untouched) ────────

/**
 * Mint a token to act as `username`; the caller opens it in a new tab. A reason
 * is required — impersonation is a break-glass action and the audit log records
 * why (ADR 0160).
 */
export async function impersonate(username: string, reason: string): Promise<string> {
  const body = await sendJSON<{ token: string; user: AuthUser; impersonator: string }>(
    "POST",
    "/api/v1/auth/impersonate",
    { username, reason },
  );
  return body.token;
}

/**
 * Record that this impersonation session ended (ADR 0160). Called from the
 * impersonated tab (whose header carries the token) before it drops the token.
 * Best-effort — the audit trail is a supplement, never a blocker on getting out.
 */
export async function endImpersonation(): Promise<void> {
  try {
    await sendJSON<{ ok: boolean }>("POST", "/api/v1/auth/impersonate/end");
  } catch {
    /* best-effort: never trap the operator in the impersonation tab */
  }
}

/**
 * The impersonation token's expiry as epoch ms, or null when this tab holds no
 * token / it can't be decoded. Drives the countdown in the impersonation banner
 * — the session is deliberately short (ADR 0160).
 */
export function impersonationExpiry(): number | null {
  const tok = impersonationToken();
  if (!tok) return null;
  try {
    let b64 = tok.split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "="; // RawURLEncoding drops padding
    const claims = JSON.parse(atob(b64)) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** Mint a token to render `view` of `tenant`; the caller opens it in a new tab. */
export async function impersonateView(tenant: string, view: ViewName): Promise<string> {
  const body = await sendJSON<{ token: string; tenant: string; view: ViewName }>(
    "POST",
    "/api/v1/auth/impersonate-view",
    { tenant, view },
  );
  return body.token;
}

/** The tenants × views the caller is allowed to impersonate. */
export function fetchImpersonableViews(): Promise<ImpersonableTenantViews[]> {
  return getJSON<{ impersonable: ImpersonableTenantViews[] }>(
    "/api/v1/authz/impersonable-views",
  ).then((b) => b.impersonable);
}

// ── tenants ──────────────────────────────────────────────────────────────────

export function fetchTenants(): Promise<Tenant[]> {
  return getJSON<{ tenants: Tenant[] }>("/api/v1/tenants").then((b) => b.tenants);
}

export async function createTenant(id: string, name: string): Promise<Tenant> {
  const body = await sendJSON<{ tenant: Tenant }>("POST", "/api/v1/tenants", { id, name });
  return body.tenant;
}

export function fetchTenantMembers(tenantId: string): Promise<TenantMember[]> {
  return getJSON<{ members: TenantMember[] }>(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/members`,
  ).then((b) => b.members);
}

export async function setTenantMember(
  tenantId: string,
  username: string,
  role: "member" | "admin",
  grant: boolean,
): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", `/api/v1/tenants/${encodeURIComponent(tenantId)}/members`, {
    username,
    role,
    grant,
  });
}

// ── users ────────────────────────────────────────────────────────────────────

export function fetchUsers(): Promise<AuthUser[]> {
  return getJSON<{ users: AuthUser[] }>("/api/v1/users").then((b) => b.users);
}

export interface NewUser {
  username: string;
  password?: string;
  displayName?: string;
  email?: string;
}

export async function createUser(user: NewUser): Promise<AuthUser> {
  const body = await sendJSON<{ user: AuthUser }>("POST", "/api/v1/users", user);
  return body.user;
}

export async function updateUser(
  username: string,
  fields: { displayName?: string; email?: string; password?: string },
): Promise<void> {
  await sendJSON<{ user: AuthUser }>("PUT", `/api/v1/users/${encodeURIComponent(username)}`, fields);
}

export async function deleteUser(username: string): Promise<void> {
  const resp = await apiFetch(`/api/v1/users/${encodeURIComponent(username)}`, { method: "DELETE" });
  if (!resp.ok) {
    let message = `API returned ${resp.status}`;
    try {
      const data = (await resp.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new ApiError(resp.status, message);
  }
}

// ── authz administration ─────────────────────────────────────────────────────

/** A user's full authz picture (roles per domain + view-impersonation grants). */
export function fetchUserAuthz(username: string): Promise<UserAuthz> {
  return getJSON<UserAuthz>(`/api/v1/authz/users/${encodeURIComponent(username)}`);
}

export interface AuthzTenantRole {
  tenant: string;
  role: string;
}
export interface AuthzTenantView {
  tenant: string;
  view: ViewName;
}
/** One user's grant summary for the consolidated Security console (LSC-302). */
export interface AuthzUserSummary {
  username: string;
  displayName?: string;
  email?: string;
  superAdmin: boolean;
  impersonateAllowed: boolean;
  /** VIP watch access (LSC-549): watch ticketed live + eligible replays without a holding. */
  vip: boolean;
  roles: AuthzTenantRole[];
  views: AuthzTenantView[];
}
export interface AuthzUsersResponse {
  users: AuthzUserSummary[];
  tenants: Tenant[];
  /** Roles grantable per tenant (super_admin excluded — it's the `superAdmin` bool). */
  grantableRoles: string[];
  views: ViewName[];
}

/** Every user + their grant summary + the option vocab, in ONE call (LSC-302) —
 *  avoids N per-user `fetchUserAuthz` fetches for the Security console grid. */
export function fetchAuthzUsers(): Promise<AuthzUsersResponse> {
  return getJSON<AuthzUsersResponse>("/api/v1/authz/users");
}

/** Grant or revoke `role` in `domain` ("*" for the global super_admin grant). */
export async function setRole(
  username: string,
  role: string,
  domain: string,
  grant: boolean,
): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", "/api/v1/authz/roles", { username, role, domain, grant });
}

/** Allow or revoke `username` impersonating `view` of `tenant`. */
export async function setImpersonationPermission(username: string, allow: boolean): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", "/api/v1/authz/impersonation", { username, allow });
}

/** Grant or revoke VIP watch access (LSC-549) — watch ticketed live + eligible replays
 *  without a ticket holding. A persistent per-user rule, not a comp holding. */
export async function setVIP(username: string, allow: boolean): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", "/api/v1/authz/vip", { username, allow });
}

export async function setViewImpersonation(
  username: string,
  tenant: string,
  view: ViewName,
  allow: boolean,
): Promise<void> {
  await sendJSON<{ ok: boolean }>("PUT", "/api/v1/authz/view-impersonation", {
    username,
    tenant,
    view,
    allow,
  });
}

/**
 * The SSO entry point is a full-page redirect (the OAuth dance), not fetch. An
 * optional same-origin `returnPath` (the cave/legend/buy page the Fan is on) is
 * passed through so the server can bind it into the signed OAuth state and the
 * callback returns the Fan there to continue a purchase (ADR 0066). "/" is the
 * default the server applies anyway, so it is left off for a clean URL. An
 * optional `buyTicketTypeId` carries the EXACT pending ticket (ADR 0103) so the
 * purchase — not just the page — resumes after sign-in; both ride the signed
 * state cookie, never the provider redirect.
 */
export function ssoStartURL(providerId: string, returnPath?: string, buyTicketTypeId?: string): string {
  const base = `/api/v1/auth/sso/${providerId}`;
  const params = new URLSearchParams();
  if (returnPath && returnPath !== "/") params.set("returnPath", returnPath);
  if (buyTicketTypeId) params.set("buy", buyTicketTypeId);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// ── Stream Deck bridge (ADR 0024) ────────────────────────────────────────────
// The Go-Live tab publishes its state and long-polls for key presses; the
// Stream Deck plugin (which runs inside the Elgato app) talks to the API on its
// own. See streamdeck-plugin/ for the plugin.

/** A key press action relayed from the deck; both are toggles. */
// Studio deck intents (ADR 0096) are string-encoded: the fixed toggles/actions
// plus parameterized ones (layout:<name>, layout-cycle:<±1>, audio:<src>:<delta>,
// audio-mute:<src>). The console parses + executes.
export type DeckAction = string;
/** One audio channel as the deck renders it on a dial. */
export interface DeckAudioChannel {
  name?: string;
  level: number; // 0-100 monitor level
  muted: boolean; // fan-facing program mute (ADR 0055)
}
export interface DeckPairCode {
  code: string;
  expiresAt: string;
}
export interface DeckPairing {
  token: string; // masked, for display
  ref: string; // URL-safe id for revoke
  label: string;
  createdAt: string;
}
export interface DeckStatePayload {
  broadcastId?: string;
  role?: "director" | "legend";
  live: boolean;
  micMuted?: boolean;
  onProgram?: boolean;
  onAir?: boolean;
  standby?: boolean; // program held/paused
  ftb?: boolean;
  layout?: string;
  boardShown?: boolean;
  nextCue?: string;
  pendingQuestions?: number;
  master?: DeckAudioChannel;
  audioA?: DeckAudioChannel;
  audioB?: DeckAudioChannel;
}
export interface DeckIntent {
  seq: number;
  action?: DeckAction;
}

/** Mint a short-lived pairing code for the operator to type into the plugin. */
export function deckPair(): Promise<DeckPairCode> {
  return sendJSON<DeckPairCode>("POST", "/api/v1/deck/pair");
}

/** The operator's paired decks (tokens masked). */
export function deckListPairings(): Promise<{ pairings: DeckPairing[] }> {
  return getJSON<{ pairings: DeckPairing[] }>("/api/v1/deck/pairings");
}

/** Revoke a paired deck by its (masked or full) token. */
export async function deckRevoke(token: string): Promise<void> {
  const resp = await apiFetch(`/api/v1/deck/pairings/${encodeURIComponent(token)}`, { method: "DELETE" });
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
}

/**
 * Publish the Go-Live tab's state so the plugin can paint its keys. Returns the
 * current intent seq — seed the long-poll cursor from the first publish so a
 * stale press from a crashed tab never replays.
 */
export async function deckPutState(state: DeckStatePayload): Promise<{ seq: number }> {
  const resp = await apiFetch("/api/v1/deck/state", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(state),
  });
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as { seq: number };
}

/** Drop the bridge row when the Go-Live tab closes (best-effort). */
export async function deckClearState(): Promise<void> {
  try {
    await apiFetch("/api/v1/deck/state", { method: "DELETE" });
  } catch {
    // teardown is best-effort — the row also ages out of presence on its own
  }
}

/**
 * Long-poll for the next key press after `since`. Resolves when the deck seq
 * advances (carrying the action) or when the server times out (seq unchanged,
 * no action). Pass an AbortSignal to cancel the wait on unmount.
 */
export async function deckWaitIntent(since: number, signal?: AbortSignal): Promise<DeckIntent> {
  const resp = await apiFetch(`/api/v1/deck/intent/wait?since=${since}`, { signal });
  if (!resp.ok) throw new ApiError(resp.status, `API returned ${resp.status}`);
  return (await resp.json()) as DeckIntent;
}
